import React from 'react';
import { USERS, PROPERTIES, SCENARIOS } from '../../firebase/index';
import PurchaseWorksheetSchema from './PurchaseWorksheetSchema';
import { getNumberFromString } from '../utility/formatNumbers';
import getSum from '../utility/getSum';
import convertUnitMoney from '../utility/convertUnitMoney';
import { yearlyPercentFromYearlyValue } from '../utility/percentageConverters';
import FormUIOptions from '../components/formBuilder/formElements/formUIOptions';
import { isValue } from '../utility/isValue';
import {
  mortgagePrincipalOutstanding,
  mortgageFormSectionCalculator,
} from '../utility/mortgageCalculator';
import { monthlyValueFromYearlyPercent } from '../utility/percentageConverters';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa6';
import { getTableSum } from '../utility/getTableSum';
import criteriaDefaults from '../constants/investmentCriteria';
import getNumberWithCommas from '../../../functions/createReport/getNumberWithCommas';

const headersInline = [
  // 'Field',
  '',
  'Projected',
  'Original',
];

const headersModal = [
  // 'Field',
  {
    heading: 'Original',
    description: '',
  },

  {
    heading: 'Rehab?',
    description: 'Is this unit being rehabbed?',
  },
  {
    heading: 'Projected Income',
    description: 'Enter the projected rent for rehabbed units',
  },
  {
    heading: 'Details',
    description:
      'Enter rehab details to accurately calculate the rehab and carrying costs',
  },
];

const beforeAfterInputs = PurchaseWorksheetSchema.inputs.schema
  .filter((section) => section.isBeforeAfter)
  .map((section) =>
    section.sectionType === 'table' || section.sectionType === 'table-stacked'
      ? {
          ...section,
          sectionLabel: section.sectionAfterLabel || section.sectionLabel,
          sectionDescription:
            section.sectionAfterDescription || section.sectionDescription,
          sectionHeadersInline: headersInline,
          sectionHeadersModal: headersModal,
          sectionType: 'before-after-table',
        }
      : {
          ...section,
          sectionLabel: section.sectionAfterLabel || section.sectionLabel,
          sectionDescription:
            section.sectionAfterDescription || section.sectionDescription,
          sectionHeadersInline: headersInline,
          sectionHeadersModal: headersModal,
          sectionType: 'before-after',
        }
  );

const rentIncomeBeforeAfter = beforeAfterInputs.find(
  (el) => el.sectionValue === 'unitDetails'
);
const otherBeforeAfter = beforeAfterInputs.filter(
  (el) => el.sectionValue !== 'unitDetails'
);

const beforeAfterCalculations = PurchaseWorksheetSchema.calculations.schema
  .filter((section) => section.isBeforeAfter)
  .map((section) =>
    section.sectionType === 'table' || section.sectionType === 'table-stacked'
      ? {
          ...section,
          sectionType: 'before-after-table',
        }
      : {
          ...section,
          sectionType: 'before-after',
        }
  );

export default {
  title: 'Scenario Parameters',
  description:
    'Use this form to enter the information about additional investment and the income and expense values that will change as the result of this scenario',
  messageOnFinish: 'Proceed to the next steps to generate a property report',
  pathToSource: [
    {
      type: 'collection',
      value: USERS,
    },
    {
      type: 'user',
      value: 'uid',
    },
    {
      type: 'collection',
      value: PROPERTIES,
    },
    {
      type: 'param',
      value: 'propertyId',
    },
    {
      type: 'collection',
      value: SCENARIOS,
    },
  ],

  inputs: {
    title: 'Rehab Worksheet',
    description:
      'Fill out the form below with the rehab details for your property',
    submitButtons: 'rehabWorksheet',
    submitButtonLabel: 'Submit & Analyze',
    submitButtonDescription: 'Submit the form to analyze the rehab details',
    sideBarSchema: true,
    schema: [
      rentIncomeBeforeAfter,
      ...otherBeforeAfter,
      {
        sectionLabel: 'Rehab Details',
        sectionDescription:
          'Add project timeline, costs, and financing details for the rehab.',
        sectionValue: 'rehabDetails',
        schema: [
          {
            type: FormUIOptions.Number,
            label: 'Project Length',
            maxValue: 999,
            description:
              'Enter the estimated overall length of the rehab project',
            groupEnd: true,
            layout: 'default',
            name: 'rehabLength',
            required: true,
            unit: 'months',
          },
          {
            type: FormUIOptions.CalculatedSection,
            name: 'rehabUnitCalculations',
            groupEnd: true,
            condition: {
              field: 'enterTotalRentalIncome',
              isLocal: true,
              value: false,
            },
            schema: [
              {
                type: FormUIOptions.Calculated,
                unit: '$',
                label: 'Unit Rehab Costs',
                description:
                  'Sum of the rehab costs for each unit you are rehabbing',
                name: 'totalUnitRehabCosts',
                derivedFrom: ['grossIncomePerUnit'],
                calcFunction: (grossIncomePerUnit) => {
                  if (Array.isArray(grossIncomePerUnit)) {
                    return grossIncomePerUnit.reduce((acc, curr) => {
                      if (curr.unitRehab) {
                        return (
                          acc +
                          getSum(getNumberFromString(curr.unitRehabCost || 0))
                        );
                      }
                      return acc;
                    }, 0);
                  } else {
                    return 0;
                  }
                },
                condition: {
                  field: 'enterTotalRentalIncome',
                  isLocal: true,
                  value: false,
                },
              },
              {
                type: FormUIOptions.Calculated,
                unit: '$',
                label: 'Unit Buyout Costs',
                description:
                  'Sum of the buyout cost for each unit you are rehabbing',
                name: 'totalUnitBuyoutCosts',
                derivedFrom: ['grossIncomePerUnit'],
                calcFunction: (grossIncomePerUnit) => {
                  if (Array.isArray(grossIncomePerUnit)) {
                    return grossIncomePerUnit.reduce((acc, curr) => {
                      if (curr.unitRehab) {
                        return (
                          acc +
                          getSum(getNumberFromString(curr.unitBuyoutCost || 0))
                        );
                      }
                      return acc;
                    }, 0);
                  } else {
                    return 0;
                  }
                },
                condition: {
                  field: 'enterTotalRentalIncome',
                  isLocal: true,
                  value: false,
                },
              },
            ],
          },

          {
            type: FormUIOptions.TableInline,
            label: 'Rehab Costs',
            description:
              'Add any additional rehab costs not covered by the rental income section',
            layout: 'default',
            itemName: 'Rehab Cost',
            name: 'additionalInvestment',
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Name',
                layout: 'default',
                name: 'additionalInvestmentItem',
                before: true,
                after: true,
              },
              {
                type: FormUIOptions.Money,
                label: 'Cost',
                layout: 'default',
                name: 'additionalInvestmentValue',
                unit: '$',
                before: true,
                after: true,
              },
            ],
          },

          {
            type: FormUIOptions.YesNo,
            label: 'Finance Rehab Costs?',
            description: 'Will you be financing the rehab costs?',
            layout: 'default',
            groupStart: true,
            name: 'isRehabFinanced',
          },
          {
            type: FormUIOptions.Money,
            label: 'Financing Amount',
            layout: 'default',
            name: 'mortgageAmountRehab',
            required: true,
            unit: '$',
            derivedFrom: ['totalRehabCosts', 'mortgageAmountRehab'],
            calcFunction: (totalRehabCosts, mortgageAmountRehab) => {
              if (isValue(mortgageAmountRehab)) {
                return mortgageAmountRehab;
              }
              return totalRehabCosts;
            },
            condition: {
              field: 'isRehabFinanced',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Pay off on refinance?',
            description:
              'Will this loan be paid off when you refinance the property?',
            name: 'payOffOnRefinanceRehab',
            defaultValue: false,
            condition: {
              field: 'isRehabFinanced',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Interest Rate',
            layout: 'default',
            name: 'interestRateRehab',
            unit: '%',
            required: true,
            condition: {
              field: 'isRehabFinanced',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest-only?',
            layout: 'default',
            name: 'mortgageInterestOnlyRehab',
            defaultValue: false,
            condition: {
              field: 'isRehabFinanced',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Loan Period',
            layout: 'default',
            name: 'interestOnlyLoanPeriodRehab',
            unit: 'years',
            condition: [
              {
                field: 'mortgageInterestOnlyRehab',
                isLocal: true,
                value: true,
              },
              {
                field: 'isRehabFinanced',
                isLocal: true,
                value: true,
              },
            ],
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest paid monthly?',
            layout: 'default',
            name: 'interestOnlyMonthlyRehab',
            description:
              'Is the interest on your loan paid monthly or at the end of the loan period?',
            defaultValue: false,
            condition: [
              {
                field: 'mortgageInterestOnlyRehab',
                isLocal: true,
                value: true,
              },
              {
                field: 'isRehabFinanced',
                isLocal: true,
                value: true,
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Amortization',
            layout: 'default',
            name: 'amortizationRehab',
            required: true,
            description: 'Financing period',
            unit: 'years',
            condition: [
              {
                field: 'mortgageInterestOnlyRehab',
                isLocal: true,
                value: false,
              },
              {
                field: 'isRehabFinanced',
                isLocal: true,
                value: true,
              },
            ],
          },

          {
            type: FormUIOptions.TableModal,
            layout: 'default',
            groupStart: true,
            groupEnd: true,
            itemName: 'Additional Financing',
            condition: {
              field: 'isRehabFinanced',
              isLocal: true,
              value: true,
            },
            label: '',
            name: 'rehabCostFinancing',
            modalDescription: 'Add information about your loan',
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Loan Name',
                layout: 'default',
                name: 'loanNameTable',
                before: true,
              },
              {
                type: FormUIOptions.Money,
                label: 'Financing Amount',
                layout: 'default',
                name: 'mortgageAmountTable',
                required: true,
                unit: '$',
                before: true,
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Pay off on refinance?',
                description:
                  'Will this loan be paid off when you refinance the property?',
                name: 'payOffOnRefinanceTable',
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Interest Rate',
                layout: 'default',
                name: 'interestRateTable',
                unit: '%',
                required: true,
                before: true,
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest-only?',
                layout: 'default',
                name: 'mortgageInterestOnlyTable',
                defaultValue: false,
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Loan Period',
                layout: 'default',
                name: 'interestOnlyLoanPeriodTable',
                unit: 'years',
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: true,
                  },
                ],
                before: true,
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest paid monthly?',
                layout: 'default',
                name: 'interestOnlyMonthlyTable',
                description:
                  'Is the interest on your loan paid monthly or at the end of the loan period?',
                defaultValue: false,
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: true,
                  },
                ],
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Amortization',
                layout: 'default',
                name: 'amortizationTable',
                description: 'Mortgage period',
                unit: 'years',
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: false,
                  },
                ],
                before: true,
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Monthly Payment',
                layout: 'default',
                name: 'monthlyPaymentTable',
                unit: '$',
                derivedFrom: [
                  'mortgageAmountTable',
                  'interestRateTable',
                  'mortgageInterestOnlyTable',
                  'interestOnlyMonthlyTable',
                  'amortizationTable',
                ],
                calcFunction: (
                  mortgageAmountTable,
                  interestRateTable,
                  mortgageInterestOnlyTable,
                  interestOnlyMonthlyTable,
                  amortizationTable
                ) => {
                  return mortgageFormSectionCalculator(
                    mortgageAmountTable,
                    interestRateTable,
                    mortgageInterestOnlyTable,
                    interestOnlyMonthlyTable,
                    amortizationTable
                  );
                },
                before: true,
              },
            ],
          },
          {
            type: FormUIOptions.CalculatedSection,
            name: 'rehabFinancingCalculations',
            schema: [
              {
                type: FormUIOptions.Calculated,
                label: 'Total Rehab Costs',
                total: true,
                layout: 'default',
                derivedFrom: [
                  'additionalInvestment',
                  'totalUnitRehabCosts',
                  'totalUnitBuyoutCosts',
                  'enterTotalRentalIncome',
                ],
                name: 'totalRehabCosts',
                unit: '$',
                calcFunction: (
                  additionalInvestment,
                  totalUnitRehabCosts,
                  totalUnitBuyoutCosts,
                  enterTotalRentalIncome
                ) => {
                  const investmentTotal = getTableSum(
                    additionalInvestment,
                    'additionalInvestmentValue'
                  );

                  if (enterTotalRentalIncome) {
                    return investmentTotal || 0;
                  }

                  return (
                    (totalUnitRehabCosts || 0) +
                    (totalUnitBuyoutCosts || 0) +
                    investmentTotal
                  );
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Total Financing Amount',
                layout: 'default',
                name: 'totalRehabFinancingAmount',
                unit: '$',
                derivedFrom: ['rehabCostFinancing', 'mortgageAmountRehab'],
                calcFunction: (rehabCostFinancing, mortgageAmountRehab) => {
                  const additionalFinancing = getTableSum(
                    rehabCostFinancing,
                    'mortgageAmountTable'
                  );
                  const rehab = mortgageAmountRehab || 0;
                  return getSum(additionalFinancing, rehab);
                },
                condition: {
                  field: 'isRehabFinanced',
                  isLocal: true,
                  value: true,
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Monthly Payment',
                layout: 'default',
                name: 'totalRehabFinancingMonthlyPayment',
                unit: '$',

                derivedFrom: [
                  'rehabCostFinancing',
                  'mortgageAmountRehab',
                  'interestRateRehab',
                  'mortgageInterestOnlyRehab',
                  'interestOnlyMonthlyRehab',
                  'amortizationRehab',
                ],
                calcFunction: (
                  rehabCostFinancing,
                  mortgageAmountRehab,
                  interestRateRehab,
                  mortgageInterestOnlyRehab,
                  interestOnlyMonthlyRehab,
                  amortizationRehab
                ) => {
                  const additionalPayments = getTableSum(
                    rehabCostFinancing,
                    'monthlyPaymentTable'
                  );
                  const payment = mortgageFormSectionCalculator(
                    mortgageAmountRehab,
                    interestRateRehab,
                    mortgageInterestOnlyRehab,
                    interestOnlyMonthlyRehab,
                    amortizationRehab
                  );
                  return getSum(additionalPayments, payment);
                },
                condition: {
                  field: 'isRehabFinanced',
                  isLocal: true,
                  value: true,
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Rehab Cost Cash',
                description: 'Total rehab costs not covered by financing',
                layout: 'default',
                name: 'rehabCostUnfinanced',
                unit: '$',
                derivedFrom: [
                  'totalRehabCosts',
                  'totalRehabFinancingAmount',
                  'isRehabFinanced',
                ],
                calcFunction: (
                  totalRehabCosts,
                  totalRehabFinancingAmount,
                  isRehabFinanced
                ) => {
                  if (!isRehabFinanced) {
                    return totalRehabCosts;
                  }
                  return totalRehabCosts - (totalRehabFinancingAmount || 0);
                },
              },
            ],
          },
        ],
      },
      {
        sectionLabel: '',
        sectionValue: 'netIncome',
        sectionNoHeader: true,
        schema: [
          {
            type: FormUIOptions.CalculatedSection,
            name: 'netIncomeCalculations',
            schema: [
              {
                type: FormUIOptions.Calculated,
                label: 'New Projected Net Income',
                name: 'newProjectedNetIncome',
                layout: 'default',
                unit: '$',
                per: 'year',
                derivedFrom: [
                  'totalGrossRentalIncome',
                  'totalOtherIncome',
                  'totalOperatingExpenses',
                ],
                calcFunction: (
                  totalGrossRentalIncome,
                  totalOtherIncome,
                  totalOperatingExpenses
                ) => {
                  const monthlyGOI = totalGrossRentalIncome + totalOtherIncome;

                  if (isValue(monthlyGOI, totalOperatingExpenses)) {
                    return Math.round(
                      (monthlyGOI - totalOperatingExpenses) * 12
                    );
                  } else {
                    return null;
                  }
                },
              },
            ],
          },
        ],
      },
      {
        sectionLabel: 'Carrying Costs',
        sectionDescription:
          'Calculate the carrying costs for the property during the rehab period',
        sectionValue: 'rehabClosingCosts',
        sectionCollapsed: true,
        sectionCondition: {
          field: 'enterTotalRentalIncome',
          value: false,
        },
        schema: [
          {
            type: FormUIOptions.YesNo,
            label: 'Calculate Using Existing Expenses?',
            layout: 'default',
            name: 'existingExpenses',
            yesLabel: 'Yes',
            noLabel: 'Customize',
            defaultValue: true,
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Enter Total Expenses?',
            layout: 'default',
            name: 'enterTotalCarryingExpenses',
            hideLabel: true,
            yesLabel: 'Total',
            noLabel: 'Itemize',
            toggleColour: 'green',
            defaultFrom: 'enterTotalExpenses',
            defaultValue: false,
            condition: {
              field: 'existingExpenses',
              isLocal: true,
              value: false,
            },
          },
          {
            type: FormUIOptions.UnitMoney,
            label: 'Total Operating Expenses',
            description:
              'Enter the total operating expenses for the property during the rehab period. Enter an amount or a percentage of the rental income.',
            layout: 'default',
            name: 'carryingExpensesNotItemized',
            selectOptions: FormUIOptions.selectOptions.monthYearPercent,
            showAltValue: true,
            derivedFrom: 'rentalIncomePreVacancy',
            defaultFrom: 'expensesNotItemized',
            condition: [
              {
                field: 'existingExpenses',
                isLocal: true,
                value: false,
              },
              {
                field: 'enterTotalCarryingExpenses',
                isLocal: true,
                value: true,
              },
            ],
          },
          {
            type: FormUIOptions.TableInline,
            label: 'Carrying Expenses',
            layout: 'default',
            name: 'carryingExpenses',
            condition: [
              {
                field: 'existingExpenses',
                isLocal: true,
                value: false,
              },
              {
                field: 'enterTotalCarryingExpenses',
                isLocal: true,
                value: false,
              },
            ],
            itemName: 'Expense',
            defaultFrom: 'operatingExpenses',
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Name',
                layout: 'default',
                name: 'expenseName',
              },
              {
                type: FormUIOptions.UnitMoney,
                label: 'Value',
                layout: 'default',
                name: 'expenseAmount',
                showAltValue: true,
                selectOptions: FormUIOptions.selectOptions.monthYearPercent,
                derivedFrom: 'rentalIncomePreVacancy',
              },
            ],
          },
          {
            type: FormUIOptions.CalculatedSection,
            name: 'carryingCostCalculations',
            groupStart: true,
            schema: [
              {
                //should be the difference between the amount of rent you would have received and the amount of rent you actually received due to the rehab
                type: FormUIOptions.Calculated,
                label: 'Total Carrying Costs',
                tooltipHeading: 'Total Carrying Costs (Estimated)',
                description:
                  'Calculated as the cash flow you would have received without rehab minus the cash flow you actually received due to the rehab, during the rehab period. For the most accurate estimate, enter accurate rehab length for each unit.',
                tooltipFormula: [
                  'Total Carrying Costs',
                  '=',
                  'Total Cash Flow Without Rehab',
                  '-',
                  'Total Cash Flow During Rehab',
                ],
                total: true,
                layout: 'default',
                name: 'totalCarryingCosts',
                unit: '$',
                condition: {
                  field: 'enterTotalRentalIncome',
                  value: [false, null, undefined],
                },
                derivedFrom: [
                  'existingExpenses',
                  'totalMonthlyOperatingExpensesPurchase',
                  'carryingExpenses',
                  'totalMonthlyMortgagePayment',
                  'totalRehabFinancingMonthlyPayment',
                  'isRehabFinanced',
                  'grossIncomePerUnit',
                  'rehabLength',
                  'enterTotalCarryingExpenses',
                  'carryingExpensesNotItemized',
                  'totalRentalIncomeOnPurchase',
                ],
                calcFunction: (
                  existingExpenses,
                  totalOperatingExpenses,
                  carryingExpenses,
                  totalMonthlyMortgagePayment,
                  totalRehabFinancingMonthlyPayment,
                  isRehabFinanced,
                  grossIncomePerUnit,
                  rehabLength,
                  enterTotalCarryingExpenses,
                  carryingExpensesNotItemized,
                  totalRentalIncomeOnPurchase
                ) => {
                  if (!isValue(rehabLength, grossIncomePerUnit)) {
                    return null;
                  }

                  // get monthly expenses based on user input
                  let monthlyExpensesTotal = 0;

                  if (existingExpenses) {
                    monthlyExpensesTotal = totalOperatingExpenses || 0;
                  } else {
                    monthlyExpensesTotal = enterTotalCarryingExpenses
                      ? carryingExpensesNotItemized
                      : getTableSum(carryingExpenses, 'expenseAmount', {
                          rentalIncomePreVacancy: totalRentalIncomeOnPurchase,
                        });
                  }

                  const monthlyMortgagePayment =
                    totalMonthlyMortgagePayment || 0;
                  const monthlyRehabFinancingPayment = isRehabFinanced
                    ? totalRehabFinancingMonthlyPayment || 0
                    : 0;

                  const totalMonthlyExpense =
                    monthlyExpensesTotal +
                    monthlyMortgagePayment +
                    monthlyRehabFinancingPayment;

                  // figure out the total expenses during rehab in both scenarios (with and without rehab)
                  const totalExpenseDuringRehab =
                    totalMonthlyExpense * rehabLength;
                  const totalExpensesWithoutRehab =
                    ((totalOperatingExpenses || 0) + monthlyMortgagePayment) *
                    rehabLength;

                  const totalIncomeWithoutRehab =
                    totalRentalIncomeOnPurchase * rehabLength;

                  let totalIncomeDuringRehab = 0;

                  if (!Array.isArray(grossIncomePerUnit)) {
                    return 0;
                  }

                  for (let i = 0; i < rehabLength; i++) {
                    totalIncomeDuringRehab += grossIncomePerUnit.reduce(
                      (acc, curr) => {
                        if (curr.unitRehab && curr.beforeRent) {
                          let vacancyObj = curr.unitRehabVacancy;

                          let vacancyMonths = 0;
                          if (vacancyObj) {
                            vacancyMonths +=
                              vacancyObj.unit === 'months'
                                ? vacancyObj.value
                                : Math.round((vacancyObj.value * 7) / 30);
                          } else {
                            vacancyMonths = rehabLength;
                          }

                          if (i < vacancyMonths) {
                            return acc;
                          } else {
                            return acc + convertUnitMoney(curr.beforeRent || 0);
                          }
                        } else if (curr.unitRehab && !curr.beforeRent) {
                          let vacancyObj = curr.unitRehabVacancy;
                          let vacancyMonths = 0;
                          if (vacancyObj) {
                            vacancyMonths +=
                              vacancyObj.unit === 'months'
                                ? vacancyObj.value
                                : Math.round((vacancyObj.value * 7) / 30);
                          } else {
                            vacancyMonths = rehabLength;
                          }

                          if (i < vacancyMonths) {
                            return acc;
                          } else {
                            return acc + convertUnitMoney(curr.rent || 0);
                          }
                        } else {
                          return acc + convertUnitMoney(curr.rent || 0);
                        }
                      },
                      0
                    );
                  }

                  const cashFlowWithoutRehab =
                    totalIncomeWithoutRehab - totalExpensesWithoutRehab;
                  const cashFlowDuringRehab =
                    totalIncomeDuringRehab - totalExpenseDuringRehab;

                  return cashFlowWithoutRehab - cashFlowDuringRehab;
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Total Rehab & Carrying Costs',
                total: true,
                layout: 'default',
                name: 'totalRehabCarryingGreater12',
                unit: '$',
                derivedFrom: ['totalRehabCosts', 'totalCarryingCosts'],
                calcFunction: (totalRehabCosts, totalCarryingCosts) => {
                  const rehab = totalRehabCosts || 0;
                  const carrying = totalCarryingCosts || 0;
                  return rehab + carrying;
                },
              },
            ],
          },
        ],
      },
      {
        sectionLabel: 'Valuation Method',
        sectionDescription:
          'Choose a method to calculate the new property value. You can modify the default selection below.',
        sectionValue: 'capRate',
        schema: [
          {
            type: FormUIOptions.Select,
            label: 'Choose an Option',
            required: true,
            description:
              'Choose an option to calculate cap rate and After-Repair Value (ARV). Default is to use the market/target cap rate.',
            layout: 'default',
            name: 'newCapRateSelect',
            defaultValue: 4,
            options: [
              {
                label: 'Use Market / Target Cap Rate',
                layout: 'default',
                value: 4,
              },
              {
                label: 'Use Previous Cap Rate',
                layout: 'default',
                value: 2,
              },
              {
                label: 'Propose a Cap Rate',
                layout: 'default',
                value: 1,
              },
              {
                label: 'Manually Enter ARV (After-Repair Value)',
                layout: 'default',
                value: 3,
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Proposed Cap Rate',
            layout: 'default',
            name: 'newCapRate',
            unit: '%',
            description: 'Learn More to calculate new cap rate',
            required: true,
            condition: {
              field: 'newCapRateSelect',
              isLocal: true,
              value: 1,
            },
          },
          {
            type: FormUIOptions.Calculated,
            label: 'Previous Cap Rate',
            description: 'Previously calculated cap rate',
            layout: 'default',
            name: 'previousCapRate',
            unit: '%',
            decimals: 2,
            derivedFrom: ['capRateInitial'],
            calcFunction: (capRate) => capRate,
            condition: {
              field: 'newCapRateSelect',
              isLocal: true,
              value: 2,
            },
          },
          {
            type: FormUIOptions.Calculated,
            label: 'Local Cap Rate',
            description: 'Previously entered local cap rate',
            layout: 'default',
            name: 'localCapRate',
            unit: '%',
            decimals: 2,
            derivedFrom: ['localCapRate'],
            calcFunction: (capRate) => capRate,
            condition: {
              field: 'newCapRateSelect',
              isLocal: true,
              value: 4,
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'ARV (After-Repair Value)',
            required: true,
            description:
              'Manually enter the after repair value of the property',
            layout: 'default',
            name: 'manualARV',
            condition: {
              field: 'newCapRateSelect',
              isLocal: true,
              value: 3,
            },
          },
          {
            type: FormUIOptions.CalculatedSection,
            name: 'valuationCalculations',
            groupStart: true,
            schema: [
              {
                type: FormUIOptions.Calculated,
                label: 'Target Value (Post-Rehab)',
                layout: 'default',
                name: 'newMarketValue',
                tooltipHeading: 'Target Value / New Value',
                description: `Based on net operating income and cap rate, or the manually entered ARV. Includes market rent for rehabbed units only.`,
                tooltipFormula: [
                  'New Value',
                  '=',
                  '(Net Annual Operating Income',
                  '/',
                  'Cap Rate)',
                  '+',
                  'Passive Appreciation',
                ],
                emphasize: true,
                large: true,
                unit: '$',
                derivedFrom: [
                  'totalGrossRentalIncome',
                  'totalOtherIncome',
                  'totalOperatingExpenses',
                  'newCapRate',
                  'previousCapRate',
                  'localCapRate',
                  'manualARV',
                  'newCapRateSelect',
                ],
                calcFunction: (
                  totalGrossRentalIncome,
                  totalOtherIncome,
                  totalOperatingExpenses,
                  newCapRate,
                  previousCapRate,
                  localCapRate,
                  manualARV,
                  newCapRateSelect
                ) => {
                  let capRate;

                  switch (newCapRateSelect) {
                    case 3:
                      return manualARV;
                    case 2:
                      capRate = previousCapRate;
                      break;
                    case 1:
                      capRate = newCapRate;
                      break;
                    case 4:
                      capRate = localCapRate;
                      break;
                  }

                  if (isValue(totalGrossRentalIncome, capRate)) {
                    const monthlyGOI =
                      (totalGrossRentalIncome || 0) + (totalOtherIncome || 0);

                    const netMonthlyIncome =
                      monthlyGOI - (totalOperatingExpenses || 0);

                    const newMarketValue = Math.round(
                      (netMonthlyIncome * 12) / (capRate / 100)
                    );

                    return newMarketValue || 0;
                  } else {
                    return null;
                  }
                },
              },
            ],
          },

          //removing potential value calculation for now
          // {
          //   type: FormUIOptions.Calculated,
          //   label: 'Potential Value (All Units Turned)',
          //   layout: 'default',
          //   name: 'newMarketValuePotential',
          //   tooltipHeading: 'Potential Value',
          //   description: `Based on net operating income and cap rate, or the manually entered ARV. Includes market rent for all units.`,
          //   tooltipFormula: [
          //     'New Value',
          //     '=',
          //     'Net Annual Operating Income',
          //     '/',
          //     'Cap Rate',
          //     '+',
          //     'Passive Appreciation',
          //   ],
          //   unit: '$',
          //   derivedFrom: [
          //     'grossIncomePerUnit',
          //     'vacancyDeduction',
          //     'rentalIncomePreVacancy',
          //     'totalOtherIncome',
          //     'totalOperatingExpenses',
          //     'newCapRate',
          //     'previousCapRate',
          //     'localCapRate',
          //     'manualARV',
          //     'newCapRateSelect',
          //     'enterTotalRentalIncome',
          //   ],
          //   calcFunction: (
          //     grossIncomePerUnit,
          //     vacancyDeduction,
          //     rentalIncomePreVacancy,
          //     totalOtherIncome,
          //     totalOperatingExpenses,
          //     newCapRate,
          //     previousCapRate,
          //     localCapRate,
          //     manualARV,
          //     newCapRateSelect,
          //     enterTotalRentalIncome
          //   ) => {
          //     if (!isValue(grossIncomePerUnit, rentalIncomePreVacancy))
          //       return null;

          //     if (enterTotalRentalIncome) {
          //       return null;
          //     }
          //     const rentalIncome =
          //       Array.isArray(grossIncomePerUnit) &&
          //       getTotalRentIncome(grossIncomePerUnit);

          //     const vacancy = isValue(vacancyDeduction)
          //       ? convertUnitMoney(vacancyDeduction, {
          //           rentalIncomePreVacancy,
          //         })
          //       : 0;
          //     const grossRentalIncome =
          //       rentalIncomePreVacancy && isValue(vacancy)
          //         ? Math.round((rentalIncome - vacancy) * 100) / 100
          //         : rentalIncomePreVacancy;

          //     let capRate;

          //     switch (newCapRateSelect) {
          //       case 3:
          //         return manualARV;
          //       case 2:
          //         capRate = previousCapRate;
          //         break;
          //       case 1:
          //         capRate = newCapRate;
          //         break;
          //       case 4:
          //         capRate = localCapRate;
          //         break;
          //     }

          //     if (isValue(grossRentalIncome, totalOtherIncome, capRate)) {
          //       const monthlyGOI = grossRentalIncome + totalOtherIncome;

          //       const netMonthlyIncome = monthlyGOI - totalOperatingExpenses;

          //       const newMarketValue = Math.round(
          //         (netMonthlyIncome * 12) / (capRate / 100)
          //       );

          //       return newMarketValue || 0;
          //     } else {
          //       return null;
          //     }
          //   },
          //   condition: {
          //     field: 'enterTotalRentalIncome',
          //     isLocal: true,
          //     value: false,
          //   },
          // },
        ],
      },
    ],
  },
  calculations: {
    title: 'Scenario Summary',
    description: 'Updated metrics based on your scenario',
    schema: [
      //the first section is a ghost section that is used to calculate the market purchase price, implemented because this is not calculated in the embedded form. for future refactoring,
      //we should have a dedicated location to enter calculations in the schema that do not appear in the UI
      {
        sectionType: '',
        sectionLabel: '-',
        sectionCondition: {
          field: 'fieldThatDoesNotExist',
          value: 'valueThatDoesNotExist',
        },
        schema: [
          {
            type: FormUIOptions.Money,
            unit: '$',
            label: '',
            name: 'rehabCarryingCostsOnlyCash',
            derivedFrom: [
              'rehabCostUnfinanced',
              'totalCarryingCosts',
              'enterTotalRentalIncome',
            ],
            calcFunction: (
              rehabCostUnfinanced,
              totalCarryingCosts,
              enterTotalRentalIncome
            ) => {
              if (enterTotalRentalIncome) {
                return rehabCostUnfinanced || 0;
              }
              return (totalCarryingCosts || 0) + (rehabCostUnfinanced || 0);
            },
          },
        ],
      },
      {
        sectionType: 'diagram',
        sectionLabel: 'Rehab Summary',
        sectionValue: 'rehabSummary',
        schema: [
          {
            type: FormUIOptions.Money,
            label: 'Purchase Price',
            name: 'purchasePrice',
            doNotCalculate: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Market Value (Est)',
            name: 'marketPurchasePrice',
            doNotCalculate: true,
          },
          {
            type: FormUIOptions.Number,
            label: 'Rehab Length',
            name: 'rehabLength',
            unit: ' months',
            decimalPlaces: 0,
          },
          {
            type: FormUIOptions.Money,
            label: 'New Projected Value',
            name: 'newMarketValue',
          },
          // {
          //   type: FormUIOptions.Money,
          //   label: 'Potential Value (Best Case)',
          //   name: 'newMarketValuePotential',
          //   condition: {
          //     field: 'enterTotalRentalIncome',
          //     value: [false, undefined, ''],
          //   },
          // },
          {
            type: FormUIOptions.Money,
            label: 'Equity Difference',
            dynamic: true,
            name: 'overUnderPayment',
            doNotCalculate: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Value Increase',
            name: 'valueIncreaseActual',
            derivedFrom: ['newMarketValue', 'marketPurchasePrice'],
            calcFunction: (newMarketValue, purchasePrice) => {
              return newMarketValue - purchasePrice;
            },
            dynamic: true,
          },
          // {
          //   type: FormUIOptions.Money,
          //   label: 'Value Increase (Potential)',
          //   name: 'valueIncreasePotential',
          //   derivedFrom: ['newMarketValuePotential', 'marketPurchasePrice'],
          //   calcFunction: (newMarketValuePotential, purchasePrice) => {
          //     return newMarketValuePotential - purchasePrice;
          //   },
          //   condition: {
          //     field: 'enterTotalRentalIncome',
          //     value: [false, undefined, ''],
          //   },
          // },
        ],
      },
      {
        sectionType: 'twoColumn',
        sectionValue: 'valueSummary',
        leftSchema: {
          sectionLabel: 'Value Summary',
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'Market Purchase Price (Est)',
              tooltipHeading: 'Market Purchase Price (Est)',
              description: 'Estimated market purchase price of the property.',
              name: 'marketPurchasePrice',
              doNotCalculate: true,
            },
            {
              type: FormUIOptions.Number,
              label: 'Rehab Length',
              name: 'rehabLength',
              unit: ' months',
              decimalPlaces: 0,
            },
            {
              type: FormUIOptions.Money,
              label: 'New Value',
              tooltipHeading: 'New Value',
              description:
                'Based on previously calculated cap rate and net income, or manually entered ARV. Includes market rent for rehabbed units only.',
              name: 'newMarketValue',
              emphasize: true,
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'New Value (Potential)',
            //   tooltipHeading: 'New Value (Potential)',
            //   small: true,
            //   description:
            //     'Based on previously calculated cap rate and net income, or manually entered ARV. Includes market rent for all units (ideal scenario).',
            //   name: 'newMarketValuePotential',
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
            {
              type: FormUIOptions.Money,
              label: 'Value Increase',
              name: 'valueIncreaseActual',
              emphasize: true,
              dynamic: true,
              groupStart: true,
              total: true,

              displayPercentFrom: [
                'valueIncreaseActual',
                'marketPurchasePrice',
              ],
              displayPercentFunction: (forcedAppreciation, purchasePrice) => {
                return (
                  Math.round((forcedAppreciation / purchasePrice) * 100 * 100) /
                  100
                );
              },
              formula: (forcedAppreciation) => [`=`, `${forcedAppreciation}`],
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'Value Increase (Potential)',
            //   small: true,
            //   name: 'valueIncreasePotential',
            //   displayPercentFrom: [
            //     'valueIncreasePotential',
            //     'marketPurchasePrice',
            //   ],
            //   displayPercentFunction: (forcedAppreciation, purchasePrice) => {
            //     return (
            //       Math.round((forcedAppreciation / purchasePrice) * 100 * 100) /
            //       100
            //     );
            //   },
            //   formula: (forcedAppreciation) => [`=`, `${forcedAppreciation}`],
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
          ],
        },

        rightSchema: {
          sectionLabel: 'Equity',
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'New Value',
              emphasize: true,
              name: 'newMarketValue',
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'New Value (Potential)',
            //   name: 'newMarketValuePotential',
            //   small: true,
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
            {
              type: FormUIOptions.Money,
              label: 'Loan Amount',
              tooltipHaeading: 'Loan Amount',
              description:
                'Total amount of financing for the property, including rehab financing',
              name: 'totalFinancingAmount',
              derivedFrom: [
                'totalMortgageAmount',
                'totalRehabFinancingAmount',
                'isRehabFinanced',
              ],
              calcFunction: (
                totalMortgageAmount,
                totalRehabFinancingAmount,
                isRehabFinanced
              ) => {
                if (isRehabFinanced) {
                  return totalMortgageAmount + totalRehabFinancingAmount;
                }
                return totalMortgageAmount;
              },
              formula: (totalFinancingAmount) => ['-', totalFinancingAmount],
            },
            {
              type: FormUIOptions.Money,
              label: 'Equity',
              name: 'newEquityBuyAndHold',
              emphasize: true,
              dynamic: true,
              total: true,
              derivedFrom: ['newMarketValue', 'totalFinancingAmount'],
              calcFunction: (newMarketValue, totalMortgageAmount) => {
                return newMarketValue - totalMortgageAmount;
              },
              formula: (newEquity) => [`=`, `${newEquity}`],
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'Equity (Potential)',
            //   name: 'newEquityBuyAndHoldPotential',
            //   small: true,
            //   derivedFrom: ['newMarketValuePotential', 'totalFinancingAmount'],
            //   calcFunction: (newMarketValue, totalMortgageAmount) => {
            //     return newMarketValue - totalMortgageAmount;
            //   },
            //   formula: (newEquity) => [`=`, `${newEquity}`],
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
            {
              type: FormUIOptions.Money,
              label: 'Down Payment',
              groupStart: true,
              name: 'downPaymentAmount',
              doNotCalculate: true,
              tooltipHeading: 'Down Payment',
              description:
                'Amount of the original downpayment, minus secondary financing',
            },
            {
              type: FormUIOptions.Money,
              label: 'Appreciation (Active)',
              name: 'rehabActiveAppreciation',
              tooltipHeading: 'Active Appreciation',
              description:
                'Estimated portion of the increase in value due to rehab',
              formula: (forcedAppreciation) => [`+`, `${forcedAppreciation}`],
              derivedFrom: [
                'newMarketValue',
                'marketPurchasePrice',
                'rehabLength',
                'marketAppreciationPercent',
                'marketAppreciationAmount',
              ],
              calcFunction: (
                newMarketValue,
                marketPurchasePrice,
                rehabLength,
                marketAppreciationPercent,
                marketAppreciationAmount
              ) => {
                const valueIncrease = newMarketValue - marketPurchasePrice;
                const rehabYears = rehabLength
                  ? Math.floor(rehabLength / 12)
                  : 0;

                if (rehabYears < 1) {
                  return valueIncrease;
                } else {
                  const multiplier = marketAppreciationPercent / 100;
                  let totalPassiveAppreciation = 0;
                  let propertyValue = marketPurchasePrice;

                  for (let i = 0; i < rehabYears; i++) {
                    totalPassiveAppreciation +=
                      i === 0
                        ? marketAppreciationAmount
                        : propertyValue * multiplier;
                    propertyValue += (valueIncrease / rehabLength) * 12;
                  }
                  return valueIncrease - (totalPassiveAppreciation || 0);
                }
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Appreciation (Passive)',
              description:
                'Estimated part of the increase in value due to market appreciation (calculated for multi-year rehabs only)',
              name: 'rehabPassiveAppreciation',
              derivedFrom: [
                'valueIncreaseActual',
                'rehabActiveAppreciation',
                'rehabLength',
              ],
              calcFunction: (
                valueIncreaseActual,
                forcedAppreciation,
                rehabLength
              ) => {
                if (!rehabLength || rehabLength < 12) {
                  return 0;
                }

                return valueIncreaseActual - forcedAppreciation;
              },
              formula: (rehabPassiveAppreciation) => [
                `+`,
                `${rehabPassiveAppreciation}`,
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Rehab Financing',
              name: 'rehabFinancingAmount',
              derivedFrom: ['totalRehabFinancingAmount', 'isRehabFinanced'],
              calcFunction: (totalRehabFinancingAmount, isRehabFinanced) => {
                if (isRehabFinanced) {
                  return totalRehabFinancingAmount;
                }
                return 0;
              },
              formula: (totalRehabFinancingAmount) => [
                `-`,
                `${totalRehabFinancingAmount}`,
              ],
              condition: {
                field: 'isRehabFinanced',
                value: true,
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Over / Under Payment',
              name: 'overpaymentUnderpaymentBuyAndHold',
              tooltipHeading: 'Over / Under Payment',
              description:
                'The difference between the actual purchase price and the market purchase price',
              derivedFrom: ['purchasePrice', 'marketPurchasePrice'],
              calcFunction: (purchasePrice, marketPurchasePrice) => {
                const purchase = purchasePrice || 0;
                const market = marketPurchasePrice || 0;
                return purchase - market;
              },
              formula: (overpaymentUnderpayment) => [
                `-`,
                `${overpaymentUnderpayment}`,
              ],
              condition: {
                field: 'usePurchaseValuation',
                value: true,
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Equity',
              name: 'newEquityBuyAndHold',
              total: true,
              dynamic: true,
              emphasize: true,
              formula: (newEquity) => [`=`, `${newEquity}`],
            },
          ],
        },
        //   rightSchema: {
        //     title: 'Rehab Cost',
        //     type: 'diagram',
        //     schema: [
        //       {
        //         type: FormUIOptions.Money,
        //         label: 'New Value',
        //         name: 'newMarketValue',
        //       },
        //       {
        //         type: FormUIOptions.Number,
        //         label: 'Months to Rehab',
        //         name: 'rehabLength',
        //       },
        //       {
        //         type: FormUIOptions.Money,
        //         label: 'Original Purchase',
        //         name: 'purchasePrice',
        //       },
        //     ],
        //   },
        // },
      },
      {
        sectionType: 'chart',
        sectionLabel: 'Purchase Summary',
        schema: [
          {
            type: FormUIOptions.Money,
            label: 'Asking Price',
            name: 'askingPrice',
            doNotCalculate: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Purchase Price',
            name: 'purchasePrice',
            doNotCalculate: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Insurance Premiums',
            name: 'cmhcPremium',
            doNotCalculate: true,
            condition: [
              {
                field: 'shouldCalculateCMHC',
                value: true,
              },
              {
                field: 'cashPurchase',
                value: false,
              },
              {
                field: 'propertyInCanada',
                isLocal: true,
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.Money,
            label: 'Mortgage Amount',
            name: 'totalMortgageAmount',
            doNotCalculate: true,
            formula: (totalMortgageAmount) => ['-', totalMortgageAmount],
          },
          {
            type: FormUIOptions.Money,
            label: 'Rehab Financing',
            name: 'rehabFinancingAmount',
            condition: {
              field: 'isRehabFinanced',
              value: true,
            },
          },

          {
            type: FormUIOptions.Money,
            label: 'Down Payment',
            name: 'downPaymentAmount',
            doNotCalculate: true,
            emphasize: true,
            groupStart: true,
            total: true,
            formula: (downPaymentAmount) => ['=', downPaymentAmount],
          },

          {
            type: FormUIOptions.Money,
            label: 'Cash Invested',
            name: 'totalCashInvested',
            dropDown: true,
            unit: '$',
            derivedFrom: [
              'rehabCostUnfinanced',
              'totalCashRequiredToClose',
              'totalCarryingCosts',
              'enterTotalRentalIncome',
            ],
            calcFunction: (
              totalRehabCosts,
              totalCashRequiredToClose,
              totalCarryingCosts,
              enterTotalRentalIncome
            ) => {
              if (enterTotalRentalIncome) {
                return totalRehabCosts + totalCashRequiredToClose;
              }
              return (
                totalRehabCosts + totalCashRequiredToClose + totalCarryingCosts
              );
            },
            formula: (totalCashInvested) => [`+`, `${totalCashInvested}`],
          },
          {
            type: FormUIOptions.Money,
            label: 'Rehab Costs Cash',
            name: 'rehabCostUnfinanced',
            dropDownDependency: 'totalCashInvested',
            unit: '$',
          },

          {
            type: FormUIOptions.Money,
            label: 'Closing Cost(s)',
            doNotCalculate: true,
            dropDownDependency: 'totalCashInvested',
            layout: 'default',
            name: 'totalCashRequiredToClose',
            unit: '$',
          },
          {
            type: FormUIOptions.Money,
            label: 'Carrying Costs',
            dropDownDependency: 'totalCashInvested',
            layout: 'default',
            description:
              'Total holding/carrying costs (including mortgage payments) for the rehab period',
            name: 'totalCarryingCosts',
            unit: '$',
            condition: {
              field: 'enterTotalRentalIncome',
              value: [false, null, undefined, ''],
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Cash Required',
            total: true,
            groupStart: true,
            emphasize: true,
            name: 'totalCashInvestedBuyAndHold',
            derivedFrom: ['downPaymentAmount', 'totalCashInvested'],
            calcFunction: (downPaymentAmount, totalCashInvested) => {
              return getSum(downPaymentAmount, totalCashInvested);
            },
            formula: (totalCashInvestedOnPurchase) => [
              '=',
              totalCashInvestedOnPurchase,
            ],
          },
        ],
        chartSchema: {
          title: 'Purchase Cost',
          type: 'pie',
          schemas: [
            {
              title: 'Purchase Cost',
              schema: [
                {
                  type: FormUIOptions.Money,
                  label: 'Down Payment',
                  name: 'downPaymentAmount',
                  doNotCalculate: true,
                  unit: '$',
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Investment',
                  name: 'totalCashInvested',
                  unit: '$',
                },
              ],
            },
            {
              title: 'Investment',
              schema: [
                {
                  type: FormUIOptions.Money,
                  label: 'Closing Cost(s)',
                  name: 'totalCashRequiredToClose',
                  doNotCalculate: true,
                  unit: '$',
                },

                {
                  type: FormUIOptions.Money,
                  label: 'Carrying Cost',
                  name: 'totalCarryingCosts',
                  unit: '$',
                  condition: {
                    field: 'enterTotalRentalIncome',
                    value: [false, null, undefined, ''],
                  },
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Rehab Cost',
                  name: 'totalRehabCosts',
                  unit: '$',
                },
              ],
            },
            {
              title: 'Closing Costs',
              condition: {
                field: 'enterTotalClosingCosts',
                value: [false, null, undefined, ''],
              },
              schema: [
                {
                  type: FormUIOptions.Table,
                  label: 'Closing Costs',
                  name: 'closingCosts',
                  schema: [
                    {
                      type: FormUIOptions.Text,
                      name: 'closingCostName',
                      label: 'Closing Cost',
                    },
                    {
                      type: FormUIOptions.Money,
                      name: 'closingCostAmount',
                      unit: '$',
                    },
                  ],
                },
              ],
            },
          ],
        },
      },
      {
        sectionType: 'twoColumn',
        leftSchema: {
          sectionLabel: 'Financing Details',
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'Mortgage Amount',
              name: 'totalMortgageAmount',
              doNotCalculate: true,
              dropDown: true,
            },
            {
              type: FormUIOptions.Money,
              label: 'Mortgage Amount',
              layout: 'default',
              name: 'mortgageAmount',
              doNotCalculate: true,
              unit: '$',
              dropDownDependency: 'totalMortgageAmount',
              condition: {
                field: 'cashPurchase',
                value: false,
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Insurance Premiums',
              name: 'cmhcPremium',
              doNotCalculate: true,
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'shouldCalculateCMHC',
                  value: true,
                },
                {
                  field: 'cashPurchase',
                  value: false,
                },
                {
                  field: 'propertyInCanada',
                  isLocal: true,
                  value: [undefined, null, 1, ''],
                },
              ],
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest-Only',
              layout: 'default',
              name: 'mortgageInterestOnly',
              doNotCalculate: true,
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'hasSecondMortgage',
                  value: true,
                },
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
            },
            {
              type: FormUIOptions.Number,
              label: 'Interest Rate',
              layout: 'default',
              name: 'interestRate',
              doNotCalculate: true,
              dropDownDependency: 'totalMortgageAmount',
              unit: '%',
              condition: {
                field: 'cashPurchase',
                value: false,
              },
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest paid monthly?',
              layout: 'default',
              name: 'interestOnlyMonthly',
              doNotCalculate: true,
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'mortgageInterestOnly',
                  value: true,
                },
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan Period',
              layout: 'default',
              unit: 'years',
              name: 'interestOnlyLoanPeriod',
              doNotCalculate: true,
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'mortgageInterestOnly',
                  value: true,
                },
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
            },
            {
              type: FormUIOptions.Number,
              label: 'Amortization',
              layout: 'default',
              name: 'amortization',
              doNotCalculate: true,
              unit: 'years',
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'mortgageInterestOnly',
                  value: !true,
                },
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan To Value',
              layout: 'default',
              name: 'loanToValueFirstMortgage',
              doNotCalculate: true,
              unit: '%',
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly Payment',
              layout: 'default',
              name: 'monthlyPayment',
              doNotCalculate: true,
              unit: '$',
              groupEnd: true,
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
            },

            {
              type: FormUIOptions.Table,
              label: 'Additional Financing',
              name: 'purchaseAdditionalFinancing',
              doNotCalculate: true,
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'loanNameTable',
                  label: 'Loan Name',
                  dropDownDependency: 'totalMortgageAmount',
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Loan Amount',
                  layout: 'default',
                  name: 'mortgageAmountTable',
                  unit: '$',
                  dropDownDependency: 'totalMortgageAmount',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest-Only',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'totalMortgageAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Interest Rate',
                  layout: 'default',
                  name: 'interestRateTable',
                  dropDownDependency: 'totalMortgageAmount',
                  unit: '%',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest paid monthly?',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'totalMortgageAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan Period',
                  layout: 'default',
                  unit: 'years',
                  name: 'interestOnlyLoanPeriodTable',
                  dropDownDependency: 'totalMortgageAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Amortization',
                  layout: 'default',
                  name: 'amortizationTable',
                  unit: 'years',
                  dropDownDependency: 'totalMortgageAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: false,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan To Value',
                  layout: 'default',
                  name: 'loanToValueTable',
                  unit: 'years',
                  dropDownDependency: 'totalMortgageAmount',
                  mainValue: 'percent',
                  displayPercentFrom: [
                    'mortgageAmountTable',
                    'marketPurchasePrice',
                  ],
                  displayPercentFunction: (
                    mortgageAmountTable,
                    purchasePrice
                  ) => {
                    return (
                      yearlyPercentFromYearlyValue(mortgageAmountTable, [
                        purchasePrice,
                      ]) || ' -'
                    );
                  },
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Monthly Payment',
                  layout: 'default',
                  name: 'monthlyPaymentTable',
                  groupEnd: true,
                  unit: '$',
                  dropDownDependency: 'totalMortgageAmount',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Rehab Financing',
              name: 'rehabFinancingAmount',
              dropDown: true,
              condition: {
                field: 'isRehabFinanced',
                value: true,
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Loan Amount',
              layout: 'default',
              name: 'mortgageAmountRehab',
              unit: '$',
              dropDownDependency: 'rehabFinancingAmount',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Pay off on refinance?',
              name: 'payOffOnRefinanceRehab',
              dropDownDependency: 'rehabFinancingAmount',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest-Only',
              layout: 'default',
              name: 'mortgageInterestOnlyRehab',
              dropDownDependency: 'rehabFinancingAmount',
              condition: {
                field: 'mortgageInterestOnlyTable',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Interest Rate',
              layout: 'default',
              name: 'interestRateRehab',
              dropDownDependency: 'rehabFinancingAmount',
              unit: '%',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest paid monthly?',
              layout: 'default',
              name: 'mortgageInterestOnlyRehab',
              dropDownDependency: 'rehabFinancingAmount',
              condition: {
                field: 'mortgageInterestOnlyRehab',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan Period',
              layout: 'default',
              unit: 'years',
              name: 'interestOnlyLoanPeriodRehab',
              dropDownDependency: 'rehabFinancingAmount',
              condition: {
                field: 'mortgageInterestOnlyRehab',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Amortization',
              layout: 'default',
              name: 'amortizationRehab',
              unit: 'years',
              dropDownDependency: 'rehabFinancingAmount',
              condition: {
                field: 'mortgageInterestOnlyRehab',
                value: false,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan To Value',
              layout: 'default',
              name: 'loanToValueRehab',
              unit: 'years',
              dropDownDependency: 'rehabFinancingAmount',
              mainValue: 'percent',
              displayPercentFrom: ['mortgageAmountRehab', 'newMarketValue'],
              displayPercentFunction: (mortgageAmountTable, purchasePrice) => {
                return (
                  yearlyPercentFromYearlyValue(mortgageAmountTable, [
                    purchasePrice,
                  ]) || ' -'
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly Payment',
              layout: 'default',
              name: 'monthlyPaymentRehab',
              derivedFrom: [
                'mortgageAmountRehab',
                'interestRateRehab',
                'mortgageInterestOnlyRehab',
                'interestOnlyMonthlyRehab',
                'amortizationRehab',
              ],
              calcFunction: (
                mortgageAmountRehab,
                interestRateRehab,
                mortgageInterestOnlyRehab,
                interestOnlyMonthlyRehab,
                amortizationRehab
              ) => {
                return mortgageFormSectionCalculator(
                  mortgageAmountRehab,
                  interestRateRehab,
                  mortgageInterestOnlyRehab,
                  interestOnlyMonthlyRehab,
                  amortizationRehab
                );
              },
              groupEnd: true,
              unit: '$',
              dropDownDependency: 'rehabFinancingAmount',
            },
            {
              type: FormUIOptions.Table,
              label: '',
              name: 'rehabCostFinancing',
              dropDownDependency: 'rehabFinancingAmount',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'loanNameTable',
                  label: 'Loan Name',
                  dropDownDependency: 'rehabFinancingAmount',
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Loan Amount',
                  layout: 'default',
                  name: 'mortgageAmountTable',
                  unit: '$',
                  dropDownDependency: 'rehabFinancingAmount',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Pay off on refinance?',
                  name: 'payOffOnRefinanceTable',
                  dropDownDependency: 'rehabFinancingAmount',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest-Only',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'rehabFinancingAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Interest Rate',
                  layout: 'default',
                  name: 'interestRateTable',
                  dropDownDependency: 'rehabFinancingAmount',
                  unit: '%',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest paid monthly?',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'rehabFinancingAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan Period',
                  layout: 'default',
                  unit: 'years',
                  name: 'interestOnlyLoanPeriodTable',
                  dropDownDependency: 'rehabFinancingAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Amortization',
                  layout: 'default',
                  name: 'amortizationTable',
                  unit: 'years',
                  dropDownDependency: 'rehabFinancingAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: false,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan To Value',
                  layout: 'default',
                  name: 'loanToValueTable',
                  unit: 'years',
                  dropDownDependency: 'rehabFinancingAmount',
                  mainValue: 'percent',
                  displayPercentFrom: ['mortgageAmountTable', 'newMarketValue'],
                  displayPercentFunction: (
                    mortgageAmountTable,
                    purchasePrice
                  ) => {
                    return (
                      yearlyPercentFromYearlyValue(mortgageAmountTable, [
                        purchasePrice,
                      ]) || ' -'
                    );
                  },
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Monthly Payment',
                  layout: 'default',
                  name: 'monthlyPaymentTable',
                  groupEnd: true,
                  unit: '$',
                  dropDownDependency: 'rehabFinancingAmount',
                },
              ],
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan to Value (LTV)',
              name: 'loanToValueBuyAndHold',
              description:
                'The loan amount as a percentage of the property value',
              tooltipHeading: 'Loan to Value (LTV)',
              tooltipFormula: [
                'LTV',
                '=',
                'Loan Amount',
                '/',
                'Property Value',
              ],
              unit: '%',
              derivedFrom: ['totalFinancingAmount', 'newMarketValue'],
              calcFunction: (totalFinancingAmount, newMarketValue) => {
                return yearlyPercentFromYearlyValue(totalFinancingAmount, [
                  newMarketValue,
                ]);
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly Payment',
              layout: 'default',
              name: 'monthlyDebtServiceBuyAndHold',
              derivedFrom: [
                'monthlyDebtService',
                'totalRehabFinancingMonthlyPayment',
                'isRehabFinanced',
              ],
              calcFunction: (
                monthlyDebtService,
                totalRehabFinancingMonthlyPayment,
                isRehabFinanced
              ) => {
                if (isRehabFinanced) {
                  return monthlyDebtService + totalRehabFinancingMonthlyPayment;
                }
                return monthlyDebtService;
              },
              emphasize: true,
              unit: '$',
            },
          ],
        },
        rightSchema: {
          sectionLabel: 'Cash Flow',
          sectionSubLabel: 'Monthly',
          annualValue: true,
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'Gross Rent',
              name: 'totalRentalIncomeBuyAndHold',
              tooltipHeading: 'Gross Rent',
              description: 'Rental income across all units',
              unit: '$',
              derivedFrom: ['rentalIncomePreVacancy'],
              calcFunction: (rentalIncomePreVacancy) => {
                return rentalIncomePreVacancy;
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Vacancy',
              layout: 'default',
              name: 'vacancy',
              unit: '$',
              derivedFrom: ['vacancyDeduction'],
              calcFunction: (vacancyDeduction) => {
                const vacancy = convertUnitMoney(vacancyDeduction);
                if (isValue(vacancy)) {
                  return Math.round(vacancy * 100) / 100;
                } else {
                  return 0;
                }
              },
              formula: (vacancy) => ['-', `${vacancy}`],
              displayPercentFrom: [
                'vacancyDeduction',
                'totalRentalIncomeBuyAndHold',
              ],
              displayPercentFunction: (vacancyDeduction, totalRentalIncome) => {
                if (isValue(vacancyDeduction) && isValue(totalRentalIncome)) {
                  return Math.round(
                    (vacancyDeduction / totalRentalIncome) * 100
                  );
                } else {
                  return 0;
                }
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Additional Income',
              name: 'totalAdditionalIncomeBuyAndHold',
              dropDown: true,
              tooltipHeading: 'Additional Income',
              description: 'Additional income except for rent across all units',
              unit: '$',
              derivedFrom: ['totalOtherIncome'],
              calcFunction: (totalOtherIncome) => totalOtherIncome,
              formula: (totalAdditionalIncome) => [
                '+',
                `${totalAdditionalIncome}`,
              ],
            },
            {
              type: FormUIOptions.Table,
              label: '',
              name: 'otherPropertyIncome',
              dropDownDependency: 'totalAdditionalIncomeBuyAndHold',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
                {
                  field: 'enterTotalOtherIncome',
                  value: [false, null, undefined, ''],
                },
              ],
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'otherIncomeName',
                  label: 'Other Income',
                  dropDownDependency: 'totalAdditionalIncomeBuyAndHold',
                },
                {
                  type: FormUIOptions.UnitMoney,
                  label: 'Amount',
                  layout: 'default',
                  name: 'otherIncomeAmount',
                  unit: '$',
                  dropDownDependency: 'totalAdditionalIncomeBuyAndHold',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Gross Income (GOI)',
              name: 'monthlyGOI',
              emphasize: true,
              tooltipHeading: 'Gross Operating Income',
              description:
                'Rental income across all units adjusted for vacancy rate',
              unit: '$',
              derivedFrom: [
                'totalGrossRentalIncome',
                'totalAdditionalIncomeBuyAndHold',
              ],
              calcFunction: (
                totalGrossRentalIncome,
                totalAdditionalIncomeBuyAndHold
              ) => {
                return getSum(
                  totalGrossRentalIncome,
                  totalAdditionalIncomeBuyAndHold
                );
              },
              formula: (monthlyGOI) => ['=', `${monthlyGOI}`],
              total: true,
            },
            {
              type: FormUIOptions.Money,
              label: 'Total Operating Expenses',
              layout: 'default',
              name: 'totalMonthlyOperatingExpenses',
              dropDown: true,
              unit: '$',
              derivedFrom: ['totalOperatingExpenses'],
              calcFunction: (totalOperatingExpenses) => totalOperatingExpenses,
              formula: (totalMonthlyOperatingExpenses) => [
                '-',
                `${totalMonthlyOperatingExpenses}`,
              ],
            },
            {
              type: FormUIOptions.Table,
              label: '',
              name: 'operatingExpenses',
              dropDownDependency: 'totalMonthlyOperatingExpenses',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
                {
                  field: 'enterTotalExpenses',
                  value: [false, null, undefined, ''],
                },
              ],
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'expenseName',
                  label: 'Expense',
                  dropDownDependency: 'totalMonthlyOperatingExpenses',
                },
                {
                  type: FormUIOptions.UnitMoney,
                  derivedFrom: 'rentalIncomePreVacancy',
                  label: 'Amount',
                  layout: 'default',
                  name: 'expenseAmount',
                  unit: '$',
                  dropDownDependency: 'totalMonthlyOperatingExpenses',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Net Income (NOI)',
              layout: 'default',
              name: 'monthlyNOI',
              emphasize: true,
              tooltipHeading: 'Net Operating Income',
              description:
                'Income from all units adjusted for total operating expenses',
              unit: '$',
              derivedFrom: ['monthlyGOI', 'totalMonthlyOperatingExpenses'],
              calcFunction: (monthlyGOI, totalMonthlyOperatingExpenses) => {
                if (isValue(monthlyGOI, totalMonthlyOperatingExpenses)) {
                  return (
                    Math.round(
                      monthlyGOI * 100 - totalMonthlyOperatingExpenses * 100
                    ) / 100
                  );
                } else {
                  return null;
                }
              },
              formula: (monthlyNOI) => ['=', `${monthlyNOI}`],
              total: true,
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly Payment',
              name: 'monthlyDebtServiceBuyAndHold',
              unit: '$',
              formula: (monthlyDebtService) => ['-', `${monthlyDebtService}`],
            },
            {
              type: FormUIOptions.Money,
              label: 'Cash Flow',
              layout: 'default',
              name: 'monthlyPreTaxCashFlowBuyAndHold',
              emphasize: true,
              dynamic: true,
              unit: '$',
              derivedFrom: [
                'monthlyGOI',
                'totalMonthlyOperatingExpenses',
                'monthlyDebtServiceBuyAndHold',
                'cashPurchase',
              ],
              calcFunction: (
                monthlyGOI,
                totalMonthlyOperatingExpenses,
                totalMonthlyMortgagePayment,
                cashPurchase
              ) => {
                if (
                  isValue(
                    monthlyGOI,
                    totalMonthlyOperatingExpenses,
                    totalMonthlyMortgagePayment
                  )
                ) {
                  const monthlyPreTaxIncome =
                    monthlyGOI - totalMonthlyOperatingExpenses;

                  if (cashPurchase) {
                    return monthlyPreTaxIncome;
                  }

                  const monthlyPreTaxCashFlow =
                    monthlyPreTaxIncome - totalMonthlyMortgagePayment;

                  const result = monthlyPreTaxCashFlow;
                  return result;
                } else {
                  return null;
                }
              },
              formula: (annualPreTaxCashFlow) => [
                '=',
                `${annualPreTaxCashFlow}`,
              ],
            },
          ],
        },
      },
      {
        sectionType: 'twoColumn',
        layoutClasses: 'order-first',
        leftSchema: {
          sectionLabel: 'Investment Metrics',
          percentageValue: true,
          annualValue: true,
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'Cash on Cash',
              dynamic: true,
              tooltipHeading: 'Cash on Cash',
              description:
                'Annual pre-tax cash flow expressed as a percentage ',
              tooltipFormula: [
                'Cash on Cash',
                '=',
                'Annual Pre-Tax Cash Flow',
                '/',
                '(Total Cash Invested',
                '+',
                'Rehab Financing)',
              ],
              layout: 'default',
              mainValue: 'percent',
              showMonthly: false,
              showAnnual: false,
              name: 'monthlyPreTaxCashFlowBuyAndHold',
              unit: '$',
              displayPercentFrom: [
                'monthlyPreTaxCashFlowBuyAndHold',
                'totalCashInvestedBuyAndHold',
                'rehabFinancingAmount',
              ],
              displayPercentFunction: (
                monthlyPreTaxCashFlowBuyAndHold,
                totalCashInvestedBuyAndHold,
                rehabFinancingAmount
              ) => {
                return (
                  yearlyPercentFromYearlyValue(
                    monthlyPreTaxCashFlowBuyAndHold * 12,
                    [totalCashInvestedBuyAndHold, rehabFinancingAmount]
                  ) || ' -'
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Principal Recapture',
              layout: 'default',
              name: 'yearOnePrincipalReduction',
              mainValue: 'percent',
              tooltipHeading: 'Principal Recapture',
              description:
                'Shows how much of the borrowed amount is paid back in the first 12 months, expressed as a percentage of the total cash invested (hover over the value to see the dollar amount)',
              unit: '$',
              isAnnual: true,
              derivedFrom: [
                'interestRate',
                'mortgageAmount',
                'monthlyPayment',
                'cashPurchase',
                'purchaseAdditionalFinancing',
              ],
              calcFunction: (
                interestRate,
                mortgageAmount,
                monthlyPayment,
                cashPurchase,
                purchaseAdditionalFinancing
              ) => {
                if (cashPurchase) {
                  return null;
                }

                if (
                  isValue(interestRate, mortgageAmount, monthlyPayment || null)
                ) {
                  const firstMortgageOutstandingAmount =
                    mortgagePrincipalOutstanding(
                      interestRate,
                      getNumberFromString(mortgageAmount),
                      getNumberFromString(monthlyPayment),
                      12
                    );

                  const firstMortgageReduction =
                    Math.round(
                      getNumberFromString(mortgageAmount) * 100 -
                        firstMortgageOutstandingAmount * 100
                    ) / 100;

                  let additionalMortgageReduction = 0;

                  if (Array.isArray(purchaseAdditionalFinancing)) {
                    purchaseAdditionalFinancing.forEach((loan) => {
                      const {
                        mortgageAmountTable,
                        monthlyPaymentTable,
                        interestRateTable,
                      } = loan;
                      if (
                        !isValue(
                          mortgageAmountTable,
                          monthlyPaymentTable || null,
                          interestRateTable
                        )
                      ) {
                        return;
                      }

                      const outstandingAmount = mortgagePrincipalOutstanding(
                        interestRateTable,
                        getNumberFromString(mortgageAmountTable),
                        getNumberFromString(monthlyPaymentTable),
                        12
                      );

                      additionalMortgageReduction +=
                        Math.round(
                          getNumberFromString(mortgageAmountTable) * 100 -
                            outstandingAmount * 100
                        ) / 100;
                    });
                  }

                  return firstMortgageReduction + additionalMortgageReduction;
                } else {
                  return null;
                }
              },
              displayPercentFrom: [
                'yearOnePrincipalReduction',
                'totalCashInvestedBuyAndHold',
                'rehabFinancingAmount',
              ],
              displayPercentFunction: (
                yearOnePrincipalReduction,
                totalCashInvestedBuyAndHold,
                rehabFinancingAmount
              ) => {
                return (
                  yearlyPercentFromYearlyValue(yearOnePrincipalReduction, [
                    totalCashInvestedBuyAndHold,
                    rehabFinancingAmount,
                  ]) || ' -'
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Passive Appreciation',
              layout: 'default',
              name: 'marketAppreciationAmount',
              unit: '$',
              tooltipHeading: 'Passive Appreciation',
              description:
                'Annual passive appreciation, based on the passive appreciation estimate.',
              tooltipFormula: [
                'Passive Appreciation',
                '=',
                'Property Value',
                'x',
                'Passive Appreciation Percentage',
                '/',
                '(Total Cash Invested',
                '+',
                'Rehab Financing)',
              ],
              mainValue: 'percent',
              isAnnual: true,
              derivedFrom: ['marketAppreciationPercent', 'newMarketValue'],
              calcFunction: (marketAppreciationPercent, purchasePrice) => {
                if (isValue(marketAppreciationPercent, purchasePrice)) {
                  const result =
                    (purchasePrice * marketAppreciationPercent) / 100;
                  return result;
                } else {
                  return null;
                }
              },
              displayPercentFrom: [
                'marketAppreciationAmount',
                'totalCashInvestedBuyAndHold',
                'rehabFinancingAmount',
              ],
              displayPercentFunction: (
                marketAppreciationAmount,
                totalCashInvestedBuyAndHold,
                rehabFinancingAmount
              ) => {
                return (
                  yearlyPercentFromYearlyValue(marketAppreciationAmount, [
                    totalCashInvestedBuyAndHold,
                    rehabFinancingAmount,
                  ]) || ' -'
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Forced Appreciation',
              name: 'forcedAppreciationBuyAndHold',
              tooltipHeading: 'Forced Appreciation',
              description:
                'Increase in the market value of the property. This is the difference between the market value and the purchase price.',
              tooltipFormula: [
                'Forced Appreciation',
                '=',
                '(Market Value',
                '-',
                'Market Purchase Price)',
                '/',
                '(Total Cash Invested',
                '+',
                'Rehab Financing)',
              ],
              unit: '$',
              mainValue: 'percent',
              isAnnual: true,
              showMonthly: false,
              dynamic: true,
              derivedFrom: ['newMarketValue', 'marketPurchasePrice'],
              calcFunction: (newMarketValue, marketPurchasePrice) => {
                return newMarketValue - marketPurchasePrice;
              },
              displayPercentFrom: [
                'forcedAppreciationBuyAndHold',
                'totalCashInvestedBuyAndHold',
                'rehabFinancingAmount',
              ],
              displayPercentFunction: (
                forcedAppreciationBuyAndHold,
                totalCashInvestedBuyAndHold,
                rehabFinancingAmount
              ) => {
                return (
                  yearlyPercentFromYearlyValue(forcedAppreciationBuyAndHold, [
                    totalCashInvestedBuyAndHold,
                    rehabFinancingAmount,
                  ]) || ' -'
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Total ROI',
              isAnnual: true,
              groupEnd: true,
              emphasize: true,
              layout: 'default',
              mainValue: 'percent',
              name: 'totalROIYear1BuyandHold',
              tooltipHeading: 'Total ROI',
              description: 'Total ROI After Rehab.',
              tooltipFormula: [
                'Total ROI',
                '=',
                '(Cash on Cash',
                '+',
                'Principal Recapture',
                '+',
                'Passive Appreciation',
                '+',
                'Forced Appreciation)',
                '/',
                '(Total Cash Invested',
                '+',
                'Rehab Financing)',
              ],
              unit: '$',
              derivedFrom: [
                'yearOnePrincipalReduction',
                'marketAppreciationAmount',
                'forcedAppreciationBuyAndHold',
                'monthlyPreTaxCashFlowBuyAndHold',
              ],
              calcFunction: (
                yearOnePrincipalReduction,
                marketAppreciationAmount,
                forcedAppreciationBuyAndHold,
                monthlyPreTaxCashFlowBuyAndHold
              ) => {
                const totalROI = getSum(
                  yearOnePrincipalReduction,
                  marketAppreciationAmount,
                  forcedAppreciationBuyAndHold,
                  monthlyPreTaxCashFlowBuyAndHold * 12
                );
                return totalROI;
              },
              displayPercentFrom: [
                'totalROIYear1BuyandHold',
                'totalCashInvestedBuyAndHold',
                'rehabFinancingAmount',
              ],
              displayPercentFunction: (
                totalROIYear1BuyandHold,
                totalCashInvestedBuyAndHold,
                rehabFinancingAmount
              ) => {
                return (
                  yearlyPercentFromYearlyValue(totalROIYear1BuyandHold, [
                    totalCashInvestedBuyAndHold,
                    rehabFinancingAmount,
                  ]) || ' -'
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Cap Rate (Purchase)',
              layout: 'default',
              name: 'capRate',
              mainValue: 'percent',
              description: 'Cap rate based on the purchase price',
              tooltipHeading: 'Cap Rate (Purchase)',
              tooltipFormula: [
                'Cap Rate (Purchase)',
                '=',
                'Net Operating Income',
                '/',
                'Purchase Price',
              ],
              showMonthly: false,
              showAnnual: false,
              unit: '%',
              isAnnual: true,
              derivedFrom: ['monthlyNOI', 'purchasePrice'],
              calcFunction: (monthlyNOI, purchasePrice) => {
                return (
                  Math.round(
                    (getNumberFromString(monthlyNOI) * 12 * 10000) /
                      purchasePrice
                  ) / 100
                );
              },
              displayMonthlyFrom: ['capRate', 'purchasePrice'],
              displayMonthlyFunction: (capRate, purchasePrice) => {
                return monthlyValueFromYearlyPercent(capRate, [purchasePrice]);
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Cap Rate (Market)',
              layout: 'default',
              name: 'marketCapRate',
              mainValue: 'percent',
              showMonthly: false,
              showAnnual: false,
              description: 'Cap rate based on the new market value',
              tooltipHeading: 'Cap Rate (Market)',
              tooltipFormula: [
                'Cap Rate (Market)',
                '=',
                'Net Operating Income',
                '/',
                'New Market Value',
              ],
              unit: '%',
              isAnnual: true,
              derivedFrom: ['monthlyNOI', 'newMarketValue'],
              calcFunction: (monthlyNOI, newMarketValue) => {
                return (
                  Math.round(
                    (getNumberFromString(monthlyNOI) * 12 * 10000) /
                      newMarketValue
                  ) / 100
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Value Increase',
              name: 'valueIncreaseActual',
              tooltipHeading: 'Property Value Increase',
              description:
                'Increase in the market value of the property. This is another way to calculate the forced appreciation, using the initial market value to calculate the percentage increase.',
              tooltipFormula: [
                'Value Increase',
                '=',
                '(New Market Value',
                '-',
                'Market Purchase Price)',
                '/',
                'Market Purchase Price',
              ],
              unit: '$',
              mainValue: 'percent',
              isAnnual: true,
              showMonthly: false,
              dynamic: true,
              displayPercentFrom: [
                'valueIncreaseActual',
                'marketPurchasePrice',
              ],
              displayPercentFunction: (forcedAppreciation, purchasePrice) => {
                return (
                  Math.round((forcedAppreciation / purchasePrice) * 100 * 100) /
                  100
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly NOI',
              tooltipHeading: 'Net Operating Income',
              showAnnualBrackets: true,
              description:
                'Net Operating Income. The total income from the property after deducting all operating expenses, but before deducting mortgage payments and taxes.',
              layout: 'default',
              name: 'monthlyNOI',
              per: 'month',
              unit: '$',
            },
          ],
        },
        rightSchema: {
          sectionLabel: 'Financial Ratios',
          sectionSubLabel: 'Hover for Details',
          schema: [
            {
              type: FormUIOptions.Number,
              label: 'Rent to Value',
              name: 'rentToValueBuyAndHold',
              tooltipHeading: 'Rent to Value',
              description: 'Annual rent as a percentage of the property value.',
              tooltipFormula: [
                'Rent to Value',
                '=',
                'Annual Rent',
                '/',
                'Market Value',
              ],
              unit: '%',
              layout: 'default',
              derivedFrom: ['monthlyGOI', 'purchasePrice'],
              calcFunction: (monthlyGOI, purchasePrice) => {
                if (isValue(monthlyGOI, purchasePrice)) {
                  return (
                    Math.round(
                      (getNumberFromString(monthlyGOI) * 12 * 10000) /
                        purchasePrice
                    ) / 100
                  );
                } else {
                  return null;
                }
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Gross Rent Multiplier',
              layout: 'default',
              name: 'grossRentMultiplierBuyAndHold',
              tooltipHeading: 'Gross Rent Multiplier',
              description:
                'The ratio between the property price and the gross rental income.',
              tooltipFormula: [
                'Gross Rent Multiplier',
                '=',
                'Market Value',
                '/',
                'Annual Rent',
              ],
              unit: '',
              derivedFrom: ['monthlyGOI', 'purchasePrice'],
              calcFunction: (monthlyGOI, purchasePrice) => {
                if (isValue(monthlyGOI, purchasePrice)) {
                  const monthlyGOIValue = getNumberFromString(monthlyGOI);
                  const purchasePriceValue = getNumberFromString(purchasePrice);

                  const yearlyGOI = monthlyGOIValue * 12;
                  const result = purchasePriceValue / yearlyGOI;
                  return Math.round(result * 100) / 100;
                } else {
                  return null;
                }
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Equity Multiple',
              layout: 'default',
              name: 'equityMultipleBuyAndHold',
              tooltipHeading: 'Equity Multiple',
              description:
                'The ratio between the total equity and the total cash invested.',
              tooltipFormula: [
                'Equity Multiple',
                '=',
                '(Total Equity',
                '+',
                'Cumulative Cash Flow)',
                '/',
                'Total Cash Invested',
              ],
              unit: '',
              derivedFrom: [
                'totalCashInvestedBuyAndHold',
                'newEquityBuyAndHold',
                'monthlyPreTaxCashFlowBuyAndHold',
              ],
              calcFunction: (
                totalCashInvestedBuyAndHold,
                newEquityBuyAndHold,
                monthlyPreTaxCashFlowBuyAndHold
              ) => {
                if (
                  !isValue(
                    totalCashInvestedBuyAndHold,
                    newEquityBuyAndHold,
                    monthlyPreTaxCashFlowBuyAndHold
                  )
                ) {
                  return null;
                }
                return (
                  Math.round(
                    ((newEquityBuyAndHold +
                      monthlyPreTaxCashFlowBuyAndHold * 12) /
                      totalCashInvestedBuyAndHold) *
                      100
                  ) / 100
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Break Even Ratio',
              name: 'breakEvenRatioBuyAndHold',
              tooltipHeading: 'Break Even Ratio',
              description:
                'The % of units that need to be occupied to break even.',
              tooltipFormula: [
                'Break Even Ratio',
                '=',
                '(Yearly Expenses',
                '+',
                'Yearly Loan Payment)',
                '/',
                'Yearly Gross Rent',
              ],
              name: 'breakEvenRatio',
              unit: '%',
              layout: 'default',
              derivedFrom: [
                'monthlyGOI',
                'monthlyDebtServiceBuyAndHold',
                'totalMonthlyOperatingExpenses',
                'cashPurchase',
              ],
              calcFunction: (
                monthlyGOI,
                monthlyDebtService,
                totalOperatingExpenses,
                cashPurchase
              ) => {
                let yearlyLoanPayment = 0;

                if (!cashPurchase && !isValue(monthlyDebtService)) {
                  return null;
                }

                if (!cashPurchase) {
                  yearlyLoanPayment = monthlyDebtService * 12;
                }

                const totalExpenses = totalOperatingExpenses * 12;
                const totalIncome = monthlyGOI * 12;

                return (
                  ((totalExpenses + yearlyLoanPayment) / totalIncome) * 100
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Debt Coverage Ratio',
              name: 'debtCoverageRatioBuyAndHold',
              tooltipHeading: 'Debt Coverage Ratio',
              description:
                'The ratio between the net operating income and the monthly debt service.',
              tooltipFormula: [
                'Debt Coverage Ratio',
                '=',
                'Net Operating Income',
                '/',
                'Debt Service',
              ],
              layout: 'default',
              dynamic: true,
              red: 1,
              green: 1.1,
              derivedFrom: [
                'monthlyNOI',
                'monthlyDebtServiceBuyAndHold',
                'cashPurchase',
              ],
              calcFunction: (monthlyNOI, monthlyDebtService, cashPurchase) => {
                if (cashPurchase) {
                  return null;
                }

                if (!isValue(monthlyNOI, monthlyDebtService)) {
                  return null;
                }

                return monthlyNOI / monthlyDebtService;
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Debt Yield',
              name: 'debtYieldBuyAndHold',
              tooltipHeading: 'Debt Yield',
              description:
                'The ratio between the net operating income and the total loan amount.',
              tooltipFormula: [
                'Debt Yield',
                '=',
                'Yearly Net Operating Income',
                '/',
                'Total Loan Amount',
              ],
              layout: 'default',
              unit: '%',
              derivedFrom: [
                'monthlyNOI',
                'totalMortgageAmount',
                'cashPurchase',
              ],
              calcFunction: (monthlyNOI, totalMortgageAmount, cashPurchase) => {
                if (cashPurchase) {
                  return null;
                }

                if (!isValue(monthlyNOI, totalMortgageAmount)) {
                  return null;
                }

                return ((monthlyNOI * 12) / totalMortgageAmount) * 100;
              },
            },
          ],
        },
      },
    ],
  },
  simpleCalculations: {
    title: 'Scenario Summary',
    description: 'Updated metrics based on your scenario',
    schema: [
      //the first section is a ghost section that is used to calculate the market purchase price, implemented because this is not calculated in the embedded form. for future refactoring,
      //we should have a dedicated location to enter calculations in the schema that do not appear in the UI
      {
        sectionType: 'twoColumn',
        leftSchema: {
          sectionLabel: 'Investment Metrics',
          percentageValue: true,
          annualValue: true,
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'Cash on Cash',
              dynamic: true,
              tooltipHeading: 'Cash on Cash',
              description:
                'Annual pre-tax cash flow expressed as a percentage ',
              tooltipFormula: [
                'Cash on Cash',
                '=',
                'Annual Pre-Tax Cash Flow',
                '/',
                '(Total Cash Invested',
                '+',
                'Rehab Financing)',
              ],
              layout: 'default',
              mainValue: 'percent',
              showMonthly: false,
              showAnnual: false,
              name: 'monthlyPreTaxCashFlowBuyAndHold',
              unit: '$',
              displayPercentFrom: [
                'monthlyPreTaxCashFlowBuyAndHold',
                'totalCashInvestedBuyAndHold',
                'rehabFinancingAmount',
              ],
              displayPercentFunction: (
                monthlyPreTaxCashFlowBuyAndHold,
                totalCashInvestedBuyAndHold,
                rehabFinancingAmount
              ) => {
                return (
                  yearlyPercentFromYearlyValue(
                    monthlyPreTaxCashFlowBuyAndHold * 12,
                    [totalCashInvestedBuyAndHold, rehabFinancingAmount]
                  ) || ' -'
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Principal Recapture',
              layout: 'default',
              name: 'yearOnePrincipalReduction',
              mainValue: 'percent',
              tooltipHeading: 'Principal Recapture',
              description:
                'Shows how much of the borrowed amount is paid back in the first 12 months, expressed as a percentage of the total cash invested (hover over the value to see the dollar amount)',
              unit: '$',
              isAnnual: true,
              derivedFrom: [
                'interestRate',
                'mortgageAmount',
                'monthlyPayment',
                'cashPurchase',
                'purchaseAdditionalFinancing',
              ],
              calcFunction: (
                interestRate,
                mortgageAmount,
                monthlyPayment,
                cashPurchase,
                purchaseAdditionalFinancing
              ) => {
                if (cashPurchase) {
                  return null;
                }

                if (
                  isValue(interestRate, mortgageAmount, monthlyPayment || null)
                ) {
                  const firstMortgageOutstandingAmount =
                    mortgagePrincipalOutstanding(
                      interestRate,
                      getNumberFromString(mortgageAmount),
                      getNumberFromString(monthlyPayment),
                      12
                    );

                  const firstMortgageReduction =
                    Math.round(
                      getNumberFromString(mortgageAmount) * 100 -
                        firstMortgageOutstandingAmount * 100
                    ) / 100;

                  let additionalMortgageReduction = 0;

                  if (Array.isArray(purchaseAdditionalFinancing)) {
                    purchaseAdditionalFinancing.forEach((loan) => {
                      const {
                        mortgageAmountTable,
                        monthlyPaymentTable,
                        interestRateTable,
                      } = loan;
                      if (
                        !isValue(
                          mortgageAmountTable,
                          monthlyPaymentTable || null,
                          interestRateTable
                        )
                      ) {
                        return;
                      }

                      const outstandingAmount = mortgagePrincipalOutstanding(
                        interestRateTable,
                        getNumberFromString(mortgageAmountTable),
                        getNumberFromString(monthlyPaymentTable),
                        12
                      );

                      additionalMortgageReduction +=
                        Math.round(
                          getNumberFromString(mortgageAmountTable) * 100 -
                            outstandingAmount * 100
                        ) / 100;
                    });
                  }

                  return firstMortgageReduction + additionalMortgageReduction;
                } else {
                  return null;
                }
              },
              displayPercentFrom: [
                'yearOnePrincipalReduction',
                'totalCashInvestedBuyAndHold',
                'rehabFinancingAmount',
              ],
              displayPercentFunction: (
                yearOnePrincipalReduction,
                totalCashInvestedBuyAndHold,
                rehabFinancingAmount
              ) => {
                return (
                  yearlyPercentFromYearlyValue(yearOnePrincipalReduction, [
                    totalCashInvestedBuyAndHold,
                    rehabFinancingAmount,
                  ]) || ' -'
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Passive Appreciation',
              layout: 'default',
              name: 'marketAppreciationAmount',
              unit: '$',
              tooltipHeading: 'Passive Appreciation',
              description:
                'Annual passive appreciation, based on the passive appreciation estimate.',
              tooltipFormula: [
                'Passive Appreciation',
                '=',
                'Property Value',
                'x',
                'Passive Appreciation Percentage',
                '/',
                '(Total Cash Invested',
                '+',
                'Rehab Financing)',
              ],
              mainValue: 'percent',
              isAnnual: true,
              derivedFrom: ['marketAppreciationPercent', 'newMarketValue'],
              calcFunction: (marketAppreciationPercent, purchasePrice) => {
                if (isValue(marketAppreciationPercent, purchasePrice)) {
                  const result =
                    (purchasePrice * marketAppreciationPercent) / 100;
                  return result;
                } else {
                  return null;
                }
              },
              displayPercentFrom: [
                'marketAppreciationAmount',
                'totalCashInvestedBuyAndHold',
                'rehabFinancingAmount',
              ],
              displayPercentFunction: (
                marketAppreciationAmount,
                totalCashInvestedBuyAndHold,
                rehabFinancingAmount
              ) => {
                return (
                  yearlyPercentFromYearlyValue(marketAppreciationAmount, [
                    totalCashInvestedBuyAndHold,
                    rehabFinancingAmount,
                  ]) || ' -'
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Forced Appreciation',
              name: 'forcedAppreciationBuyAndHold',
              tooltipHeading: 'Forced Appreciation',
              description:
                'Increase in the market value of the property. This is the difference between the market value and the purchase price.',
              tooltipFormula: [
                'Forced Appreciation',
                '=',
                '(Market Value',
                '-',
                'Market Purchase Price)',
                '/',
                '(Total Cash Invested',
                '+',
                'Rehab Financing)',
              ],
              unit: '$',
              mainValue: 'percent',
              isAnnual: true,
              showMonthly: false,
              dynamic: true,
              derivedFrom: ['newMarketValue', 'marketPurchasePrice'],
              calcFunction: (newMarketValue, marketPurchasePrice) => {
                return newMarketValue - marketPurchasePrice;
              },
              displayPercentFrom: [
                'forcedAppreciationBuyAndHold',
                'totalCashInvestedBuyAndHold',
                'rehabFinancingAmount',
              ],
              displayPercentFunction: (
                forcedAppreciationBuyAndHold,
                totalCashInvestedBuyAndHold,
                rehabFinancingAmount
              ) => {
                return (
                  yearlyPercentFromYearlyValue(forcedAppreciationBuyAndHold, [
                    totalCashInvestedBuyAndHold,
                    rehabFinancingAmount,
                  ]) || ' -'
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Total ROI',
              isAnnual: true,
              groupEnd: true,
              emphasize: true,
              layout: 'default',
              mainValue: 'percent',
              name: 'totalROIYear1BuyandHold',
              tooltipHeading: 'Total ROI',
              description: 'Total ROI After Rehab.',
              tooltipFormula: [
                'Total ROI',
                '=',
                '(Cash on Cash',
                '+',
                'Principal Recapture',
                '+',
                'Passive Appreciation',
                '+',
                'Forced Appreciation)',
                '/',
                '(Total Cash Invested',
                '+',
                'Rehab Financing)',
              ],
              unit: '$',
              derivedFrom: [
                'yearOnePrincipalReduction',
                'marketAppreciationAmount',
                'forcedAppreciationBuyAndHold',
                'monthlyPreTaxCashFlowBuyAndHold',
              ],
              calcFunction: (
                yearOnePrincipalReduction,
                marketAppreciationAmount,
                forcedAppreciationBuyAndHold,
                monthlyPreTaxCashFlowBuyAndHold
              ) => {
                const totalROI = getSum(
                  yearOnePrincipalReduction,
                  marketAppreciationAmount,
                  forcedAppreciationBuyAndHold,
                  monthlyPreTaxCashFlowBuyAndHold * 12
                );
                return totalROI;
              },
              displayPercentFrom: [
                'totalROIYear1BuyandHold',
                'totalCashInvestedBuyAndHold',
                'rehabFinancingAmount',
              ],
              displayPercentFunction: (
                totalROIYear1BuyandHold,
                totalCashInvestedBuyAndHold,
                rehabFinancingAmount
              ) => {
                return (
                  yearlyPercentFromYearlyValue(totalROIYear1BuyandHold, [
                    totalCashInvestedBuyAndHold,
                    rehabFinancingAmount,
                  ]) || ' -'
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Cap Rate (Purchase)',
              layout: 'default',
              name: 'capRate',
              mainValue: 'percent',
              description: 'Cap rate based on the purchase price',
              tooltipHeading: 'Cap Rate (Purchase)',
              tooltipFormula: [
                'Cap Rate (Purchase)',
                '=',
                'Net Operating Income',
                '/',
                'Purchase Price',
              ],
              showMonthly: false,
              showAnnual: false,
              unit: '%',
              isAnnual: true,
              derivedFrom: ['monthlyNOI', 'purchasePrice'],
              calcFunction: (monthlyNOI, purchasePrice) => {
                return (
                  Math.round(
                    (getNumberFromString(monthlyNOI) * 12 * 10000) /
                      purchasePrice
                  ) / 100
                );
              },
              displayMonthlyFrom: ['capRate', 'purchasePrice'],
              displayMonthlyFunction: (capRate, purchasePrice) => {
                return monthlyValueFromYearlyPercent(capRate, [purchasePrice]);
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Cap Rate (Market)',
              layout: 'default',
              name: 'marketCapRate',
              mainValue: 'percent',
              showMonthly: false,
              showAnnual: false,
              description: 'Cap rate based on the new market value',
              tooltipHeading: 'Cap Rate (Market)',
              tooltipFormula: [
                'Cap Rate (Market)',
                '=',
                'Net Operating Income',
                '/',
                'New Market Value',
              ],
              unit: '%',
              isAnnual: true,
              derivedFrom: ['monthlyNOI', 'newMarketValue'],
              calcFunction: (monthlyNOI, newMarketValue) => {
                return (
                  Math.round(
                    (getNumberFromString(monthlyNOI) * 12 * 10000) /
                      newMarketValue
                  ) / 100
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Value Increase',
              name: 'valueIncreaseActual',
              tooltipHeading: 'Property Value Increase',
              description:
                'Increase in the market value of the property. This is another way to calculate the forced appreciation, using the initial market value to calculate the percentage increase.',
              tooltipFormula: [
                'Value Increase',
                '=',
                '(New Market Value',
                '-',
                'Market Purchase Price)',
                '/',
                'Market Purchase Price',
              ],
              unit: '$',
              mainValue: 'percent',
              isAnnual: true,
              showMonthly: false,
              dynamic: true,
              displayPercentFrom: [
                'valueIncreaseActual',
                'marketPurchasePrice',
              ],
              displayPercentFunction: (forcedAppreciation, purchasePrice) => {
                return (
                  Math.round((forcedAppreciation / purchasePrice) * 100 * 100) /
                  100
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly NOI',
              tooltipHeading: 'Net Operating Income',
              showAnnualBrackets: true,
              description:
                'Net Operating Income. The total income from the property after deducting all operating expenses, but before deducting mortgage payments and taxes.',
              layout: 'default',
              name: 'monthlyNOI',
              per: 'month',
              unit: '$',
            },
          ],
        },
        rightSchema: {
          sectionLabel: 'Financial Ratios',
          sectionSubLabel: 'Hover for Details',
          schema: [
            {
              type: FormUIOptions.Number,
              label: 'Rent to Value',
              name: 'rentToValueBuyAndHold',
              tooltipHeading: 'Rent to Value',
              description: 'Annual rent as a percentage of the property value.',
              tooltipFormula: [
                'Rent to Value',
                '=',
                'Annual Rent',
                '/',
                'Market Value',
              ],
              unit: '%',
              layout: 'default',
              derivedFrom: ['monthlyGOI', 'purchasePrice'],
              calcFunction: (monthlyGOI, purchasePrice) => {
                if (isValue(monthlyGOI, purchasePrice)) {
                  return (
                    Math.round(
                      (getNumberFromString(monthlyGOI) * 12 * 10000) /
                        purchasePrice
                    ) / 100
                  );
                } else {
                  return null;
                }
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Gross Rent Multiplier',
              layout: 'default',
              name: 'grossRentMultiplierBuyAndHold',
              tooltipHeading: 'Gross Rent Multiplier',
              description:
                'The ratio between the property price and the gross rental income.',
              tooltipFormula: [
                'Gross Rent Multiplier',
                '=',
                'Market Value',
                '/',
                'Annual Rent',
              ],
              unit: '',
              derivedFrom: ['monthlyGOI', 'purchasePrice'],
              calcFunction: (monthlyGOI, purchasePrice) => {
                if (isValue(monthlyGOI, purchasePrice)) {
                  const monthlyGOIValue = getNumberFromString(monthlyGOI);
                  const purchasePriceValue = getNumberFromString(purchasePrice);

                  const yearlyGOI = monthlyGOIValue * 12;
                  const result = purchasePriceValue / yearlyGOI;
                  return Math.round(result * 100) / 100;
                } else {
                  return null;
                }
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Equity Multiple',
              layout: 'default',
              name: 'equityMultipleBuyAndHold',
              tooltipHeading: 'Equity Multiple',
              description:
                'The ratio between the total equity and the total cash invested.',
              tooltipFormula: [
                'Equity Multiple',
                '=',
                '(Total Equity',
                '+',
                'Cumulative Cash Flow)',
                '/',
                'Total Cash Invested',
              ],
              unit: '',
              derivedFrom: [
                'totalCashInvestedBuyAndHold',
                'newEquityBuyAndHold',
                'monthlyPreTaxCashFlowBuyAndHold',
              ],
              calcFunction: (
                totalCashInvestedBuyAndHold,
                newEquityBuyAndHold,
                monthlyPreTaxCashFlowBuyAndHold
              ) => {
                if (
                  !isValue(
                    totalCashInvestedBuyAndHold,
                    newEquityBuyAndHold,
                    monthlyPreTaxCashFlowBuyAndHold
                  )
                ) {
                  return null;
                }
                return (
                  Math.round(
                    ((newEquityBuyAndHold +
                      monthlyPreTaxCashFlowBuyAndHold * 12) /
                      totalCashInvestedBuyAndHold) *
                      100
                  ) / 100
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Break Even Ratio',
              name: 'breakEvenRatioBuyAndHold',
              tooltipHeading: 'Break Even Ratio',
              description:
                'The % of units that need to be occupied to break even.',
              tooltipFormula: [
                'Break Even Ratio',
                '=',
                '(Yearly Expenses',
                '+',
                'Yearly Loan Payment)',
                '/',
                'Yearly Gross Rent',
              ],
              name: 'breakEvenRatio',
              unit: '%',
              layout: 'default',
              derivedFrom: [
                'monthlyGOI',
                'monthlyDebtServiceBuyAndHold',
                'totalMonthlyOperatingExpenses',
                'cashPurchase',
              ],
              calcFunction: (
                monthlyGOI,
                monthlyDebtService,
                totalOperatingExpenses,
                cashPurchase
              ) => {
                let yearlyLoanPayment = 0;

                if (!cashPurchase && !isValue(monthlyDebtService)) {
                  return null;
                }

                if (!cashPurchase) {
                  yearlyLoanPayment = monthlyDebtService * 12;
                }

                const totalExpenses = totalOperatingExpenses * 12;
                const totalIncome = monthlyGOI * 12;

                return (
                  ((totalExpenses + yearlyLoanPayment) / totalIncome) * 100
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Debt Coverage Ratio',
              name: 'debtCoverageRatioBuyAndHold',
              tooltipHeading: 'Debt Coverage Ratio',
              description:
                'The ratio between the net operating income and the monthly debt service.',
              tooltipFormula: [
                'Debt Coverage Ratio',
                '=',
                'Net Operating Income',
                '/',
                'Debt Service',
              ],
              layout: 'default',
              dynamic: true,
              red: 1,
              green: 1.1,
              derivedFrom: [
                'monthlyNOI',
                'monthlyDebtServiceBuyAndHold',
                'cashPurchase',
              ],
              calcFunction: (monthlyNOI, monthlyDebtService, cashPurchase) => {
                if (cashPurchase) {
                  return null;
                }

                if (!isValue(monthlyNOI, monthlyDebtService)) {
                  return null;
                }

                return monthlyNOI / monthlyDebtService;
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Debt Yield',
              name: 'debtYieldBuyAndHold',
              tooltipHeading: 'Debt Yield',
              description:
                'The ratio between the net operating income and the total loan amount.',
              tooltipFormula: [
                'Debt Yield',
                '=',
                'Yearly Net Operating Income',
                '/',
                'Total Loan Amount',
              ],
              layout: 'default',
              unit: '%',
              derivedFrom: [
                'monthlyNOI',
                'totalMortgageAmount',
                'cashPurchase',
              ],
              calcFunction: (monthlyNOI, totalMortgageAmount, cashPurchase) => {
                if (cashPurchase) {
                  return null;
                }

                if (!isValue(monthlyNOI, totalMortgageAmount)) {
                  return null;
                }

                return ((monthlyNOI * 12) / totalMortgageAmount) * 100;
              },
            },
          ],
        },
      },
      {
        sectionType: 'twoColumn',
        sectionValue: 'valueSummary',
        leftSchema: {
          sectionLabel: 'Value Summary',
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'Market Purchase Price (Est)',
              tooltipHeading: 'Market Purchase Price (Est)',
              description: 'Estimated market purchase price of the property.',
              name: 'marketPurchasePrice',
              doNotCalculate: true,
            },
            {
              type: FormUIOptions.Number,
              label: 'Rehab Length',
              name: 'rehabLength',
              unit: ' months',
              decimalPlaces: 0,
            },
            {
              type: FormUIOptions.Money,
              label: 'New Value',
              tooltipHeading: 'New Value',
              description:
                'Based on previously calculated cap rate and net income, or manually entered ARV. Includes market rent for rehabbed units only.',
              name: 'newMarketValue',
              emphasize: true,
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'New Value (Potential)',
            //   tooltipHeading: 'New Value (Potential)',
            //   small: true,
            //   description:
            //     'Based on previously calculated cap rate and net income, or manually entered ARV. Includes market rent for all units (ideal scenario).',
            //   name: 'newMarketValuePotential',
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
            {
              type: FormUIOptions.Money,
              label: 'Value Increase',
              name: 'valueIncreaseActual',
              emphasize: true,
              dynamic: true,
              groupStart: true,
              total: true,

              displayPercentFrom: [
                'valueIncreaseActual',
                'marketPurchasePrice',
              ],
              displayPercentFunction: (forcedAppreciation, purchasePrice) => {
                return (
                  Math.round((forcedAppreciation / purchasePrice) * 100 * 100) /
                  100
                );
              },
              formula: (forcedAppreciation) => [`=`, `${forcedAppreciation}`],
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'Value Increase (Potential)',
            //   small: true,
            //   name: 'valueIncreasePotential',
            //   displayPercentFrom: [
            //     'valueIncreasePotential',
            //     'marketPurchasePrice',
            //   ],
            //   displayPercentFunction: (forcedAppreciation, purchasePrice) => {
            //     return (
            //       Math.round((forcedAppreciation / purchasePrice) * 100 * 100) /
            //       100
            //     );
            //   },
            //   formula: (forcedAppreciation) => [`=`, `${forcedAppreciation}`],
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
          ],
        },

        rightSchema: {
          sectionLabel: 'Equity',
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'New Value',
              emphasize: true,
              name: 'newMarketValue',
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'New Value (Potential)',
            //   name: 'newMarketValuePotential',
            //   small: true,
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
            {
              type: FormUIOptions.Money,
              label: 'Loan Amount',
              tooltipHaeading: 'Loan Amount',
              description:
                'Total amount of financing for the property, including rehab financing',
              name: 'totalFinancingAmount',
              derivedFrom: [
                'totalMortgageAmount',
                'totalRehabFinancingAmount',
                'isRehabFinanced',
              ],
              calcFunction: (
                totalMortgageAmount,
                totalRehabFinancingAmount,
                isRehabFinanced
              ) => {
                if (isRehabFinanced) {
                  return totalMortgageAmount + totalRehabFinancingAmount;
                }
                return totalMortgageAmount;
              },
              formula: (totalFinancingAmount) => ['-', totalFinancingAmount],
            },
            {
              type: FormUIOptions.Money,
              label: 'Equity',
              name: 'newEquityBuyAndHold',
              emphasize: true,
              dynamic: true,
              total: true,
              derivedFrom: ['newMarketValue', 'totalFinancingAmount'],
              calcFunction: (newMarketValue, totalMortgageAmount) => {
                return newMarketValue - totalMortgageAmount;
              },
              formula: (newEquity) => [`=`, `${newEquity}`],
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'Equity (Potential)',
            //   name: 'newEquityBuyAndHoldPotential',
            //   small: true,
            //   derivedFrom: ['newMarketValuePotential', 'totalFinancingAmount'],
            //   calcFunction: (newMarketValue, totalMortgageAmount) => {
            //     return newMarketValue - totalMortgageAmount;
            //   },
            //   formula: (newEquity) => [`=`, `${newEquity}`],
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
            {
              type: FormUIOptions.Money,
              label: 'Down Payment',
              groupStart: true,
              name: 'downPaymentAmount',
              doNotCalculate: true,
              tooltipHeading: 'Down Payment',
              description:
                'Amount of the original downpayment, minus secondary financing',
            },
            {
              type: FormUIOptions.Money,
              label: 'Appreciation (Active)',
              name: 'rehabActiveAppreciation',
              tooltipHeading: 'Active Appreciation',
              description:
                'Estimated portion of the increase in value due to rehab',
              formula: (forcedAppreciation) => [`+`, `${forcedAppreciation}`],
              derivedFrom: [
                'newMarketValue',
                'marketPurchasePrice',
                'rehabLength',
                'marketAppreciationPercent',
                'marketAppreciationAmount',
              ],
              calcFunction: (
                newMarketValue,
                marketPurchasePrice,
                rehabLength,
                marketAppreciationPercent,
                marketAppreciationAmount
              ) => {
                const valueIncrease = newMarketValue - marketPurchasePrice;
                const rehabYears = rehabLength
                  ? Math.floor(rehabLength / 12)
                  : 0;

                if (rehabYears < 1) {
                  return valueIncrease;
                } else {
                  const multiplier = marketAppreciationPercent / 100;
                  let totalPassiveAppreciation = 0;
                  let propertyValue = marketPurchasePrice;

                  for (let i = 0; i < rehabYears; i++) {
                    totalPassiveAppreciation +=
                      i === 0
                        ? marketAppreciationAmount
                        : propertyValue * multiplier;
                    propertyValue += (valueIncrease / rehabLength) * 12;
                  }
                  return valueIncrease - (totalPassiveAppreciation || 0);
                }
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Appreciation (Passive)',
              description:
                'Estimated part of the increase in value due to market appreciation (calculated for multi-year rehabs only)',
              name: 'rehabPassiveAppreciation',
              derivedFrom: [
                'valueIncreaseActual',
                'rehabActiveAppreciation',
                'rehabLength',
              ],
              calcFunction: (
                valueIncreaseActual,
                forcedAppreciation,
                rehabLength
              ) => {
                if (!rehabLength || rehabLength < 12) {
                  return 0;
                }

                return valueIncreaseActual - forcedAppreciation;
              },
              formula: (rehabPassiveAppreciation) => [
                `+`,
                `${rehabPassiveAppreciation}`,
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Rehab Financing',
              name: 'rehabFinancingAmount',
              derivedFrom: ['totalRehabFinancingAmount', 'isRehabFinanced'],
              calcFunction: (totalRehabFinancingAmount, isRehabFinanced) => {
                if (isRehabFinanced) {
                  return totalRehabFinancingAmount;
                }
                return 0;
              },
              formula: (totalRehabFinancingAmount) => [
                `-`,
                `${totalRehabFinancingAmount}`,
              ],
              condition: {
                field: 'isRehabFinanced',
                value: true,
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Over / Under Payment',
              name: 'overpaymentUnderpaymentBuyAndHold',
              tooltipHeading: 'Over / Under Payment',
              description:
                'The difference between the actual purchase price and the market purchase price',
              derivedFrom: ['purchasePrice', 'marketPurchasePrice'],
              calcFunction: (purchasePrice, marketPurchasePrice) => {
                const purchase = purchasePrice || 0;
                const market = marketPurchasePrice || 0;
                return purchase - market;
              },
              formula: (overpaymentUnderpayment) => [
                `-`,
                `${overpaymentUnderpayment}`,
              ],
              condition: {
                field: 'usePurchaseValuation',
                value: true,
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Equity',
              name: 'newEquityBuyAndHold',
              total: true,
              dynamic: true,
              emphasize: true,
              formula: (newEquity) => [`=`, `${newEquity}`],
            },
          ],
        },
        //   rightSchema: {
        //     title: 'Rehab Cost',
        //     type: 'diagram',
        //     schema: [
        //       {
        //         type: FormUIOptions.Money,
        //         label: 'New Value',
        //         name: 'newMarketValue',
        //       },
        //       {
        //         type: FormUIOptions.Number,
        //         label: 'Months to Rehab',
        //         name: 'rehabLength',
        //       },
        //       {
        //         type: FormUIOptions.Money,
        //         label: 'Original Purchase',
        //         name: 'purchasePrice',
        //       },
        //     ],
        //   },
        // },
      },
      {
        sectionType: 'twoColumn',
        leftSchema: {
          sectionLabel: 'Financing Details',
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'Mortgage Amount',
              name: 'totalMortgageAmount',
              doNotCalculate: true,
              dropDown: true,
            },
            {
              type: FormUIOptions.Money,
              label: 'Mortgage Amount',
              layout: 'default',
              name: 'mortgageAmount',
              doNotCalculate: true,
              unit: '$',
              dropDownDependency: 'totalMortgageAmount',
              condition: {
                field: 'cashPurchase',
                value: false,
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Insurance Premiums',
              name: 'cmhcPremium',
              doNotCalculate: true,
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'shouldCalculateCMHC',
                  value: true,
                },
                {
                  field: 'cashPurchase',
                  value: false,
                },
                {
                  field: 'propertyInCanada',
                  isLocal: true,
                  value: [undefined, null, 1, ''],
                },
              ],
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest-Only',
              layout: 'default',
              name: 'mortgageInterestOnly',
              doNotCalculate: true,
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'hasSecondMortgage',
                  value: true,
                },
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
            },
            {
              type: FormUIOptions.Number,
              label: 'Interest Rate',
              layout: 'default',
              name: 'interestRate',
              doNotCalculate: true,
              dropDownDependency: 'totalMortgageAmount',
              unit: '%',
              condition: {
                field: 'cashPurchase',
                value: false,
              },
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest paid monthly?',
              layout: 'default',
              name: 'interestOnlyMonthly',
              doNotCalculate: true,
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'mortgageInterestOnly',
                  value: true,
                },
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan Period',
              layout: 'default',
              unit: 'years',
              name: 'interestOnlyLoanPeriod',
              doNotCalculate: true,
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'mortgageInterestOnly',
                  value: true,
                },
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
            },
            {
              type: FormUIOptions.Number,
              label: 'Amortization',
              layout: 'default',
              name: 'amortization',
              doNotCalculate: true,
              unit: 'years',
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'mortgageInterestOnly',
                  value: !true,
                },
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan To Value',
              layout: 'default',
              name: 'loanToValueFirstMortgage',
              doNotCalculate: true,
              unit: '%',
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly Payment',
              layout: 'default',
              name: 'monthlyPayment',
              doNotCalculate: true,
              unit: '$',
              groupEnd: true,
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
            },

            {
              type: FormUIOptions.Table,
              label: 'Additional Financing',
              name: 'purchaseAdditionalFinancing',
              doNotCalculate: true,
              dropDownDependency: 'totalMortgageAmount',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'loanNameTable',
                  label: 'Loan Name',
                  dropDownDependency: 'totalMortgageAmount',
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Loan Amount',
                  layout: 'default',
                  name: 'mortgageAmountTable',
                  unit: '$',
                  dropDownDependency: 'totalMortgageAmount',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest-Only',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'totalMortgageAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Interest Rate',
                  layout: 'default',
                  name: 'interestRateTable',
                  dropDownDependency: 'totalMortgageAmount',
                  unit: '%',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest paid monthly?',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'totalMortgageAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan Period',
                  layout: 'default',
                  unit: 'years',
                  name: 'interestOnlyLoanPeriodTable',
                  dropDownDependency: 'totalMortgageAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Amortization',
                  layout: 'default',
                  name: 'amortizationTable',
                  unit: 'years',
                  dropDownDependency: 'totalMortgageAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: false,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan To Value',
                  layout: 'default',
                  name: 'loanToValueTable',
                  unit: 'years',
                  dropDownDependency: 'totalMortgageAmount',
                  mainValue: 'percent',
                  displayPercentFrom: [
                    'mortgageAmountTable',
                    'marketPurchasePrice',
                  ],
                  displayPercentFunction: (
                    mortgageAmountTable,
                    purchasePrice
                  ) => {
                    return (
                      yearlyPercentFromYearlyValue(mortgageAmountTable, [
                        purchasePrice,
                      ]) || ' -'
                    );
                  },
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Monthly Payment',
                  layout: 'default',
                  name: 'monthlyPaymentTable',
                  groupEnd: true,
                  unit: '$',
                  dropDownDependency: 'totalMortgageAmount',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Rehab Financing',
              name: 'rehabFinancingAmount',
              dropDown: true,
              condition: {
                field: 'isRehabFinanced',
                value: true,
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Loan Amount',
              layout: 'default',
              name: 'mortgageAmountRehab',
              unit: '$',
              dropDownDependency: 'rehabFinancingAmount',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Pay off on refinance?',
              name: 'payOffOnRefinanceRehab',
              dropDownDependency: 'rehabFinancingAmount',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest-Only',
              layout: 'default',
              name: 'mortgageInterestOnlyRehab',
              dropDownDependency: 'rehabFinancingAmount',
              condition: {
                field: 'mortgageInterestOnlyTable',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Interest Rate',
              layout: 'default',
              name: 'interestRateRehab',
              dropDownDependency: 'rehabFinancingAmount',
              unit: '%',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest paid monthly?',
              layout: 'default',
              name: 'mortgageInterestOnlyRehab',
              dropDownDependency: 'rehabFinancingAmount',
              condition: {
                field: 'mortgageInterestOnlyRehab',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan Period',
              layout: 'default',
              unit: 'years',
              name: 'interestOnlyLoanPeriodRehab',
              dropDownDependency: 'rehabFinancingAmount',
              condition: {
                field: 'mortgageInterestOnlyRehab',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Amortization',
              layout: 'default',
              name: 'amortizationRehab',
              unit: 'years',
              dropDownDependency: 'rehabFinancingAmount',
              condition: {
                field: 'mortgageInterestOnlyRehab',
                value: false,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan To Value',
              layout: 'default',
              name: 'loanToValueRehab',
              unit: 'years',
              dropDownDependency: 'rehabFinancingAmount',
              mainValue: 'percent',
              displayPercentFrom: ['mortgageAmountRehab', 'newMarketValue'],
              displayPercentFunction: (mortgageAmountTable, purchasePrice) => {
                return (
                  yearlyPercentFromYearlyValue(mortgageAmountTable, [
                    purchasePrice,
                  ]) || ' -'
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly Payment',
              layout: 'default',
              name: 'monthlyPaymentRehab',
              derivedFrom: [
                'mortgageAmountRehab',
                'interestRateRehab',
                'mortgageInterestOnlyRehab',
                'interestOnlyMonthlyRehab',
                'amortizationRehab',
              ],
              calcFunction: (
                mortgageAmountRehab,
                interestRateRehab,
                mortgageInterestOnlyRehab,
                interestOnlyMonthlyRehab,
                amortizationRehab
              ) => {
                return mortgageFormSectionCalculator(
                  mortgageAmountRehab,
                  interestRateRehab,
                  mortgageInterestOnlyRehab,
                  interestOnlyMonthlyRehab,
                  amortizationRehab
                );
              },
              groupEnd: true,
              unit: '$',
              dropDownDependency: 'rehabFinancingAmount',
            },
            {
              type: FormUIOptions.Table,
              label: '',
              name: 'rehabCostFinancing',
              dropDownDependency: 'rehabFinancingAmount',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
              ],
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'loanNameTable',
                  label: 'Loan Name',
                  dropDownDependency: 'rehabFinancingAmount',
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Loan Amount',
                  layout: 'default',
                  name: 'mortgageAmountTable',
                  unit: '$',
                  dropDownDependency: 'rehabFinancingAmount',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Pay off on refinance?',
                  name: 'payOffOnRefinanceTable',
                  dropDownDependency: 'rehabFinancingAmount',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest-Only',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'rehabFinancingAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Interest Rate',
                  layout: 'default',
                  name: 'interestRateTable',
                  dropDownDependency: 'rehabFinancingAmount',
                  unit: '%',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest paid monthly?',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'rehabFinancingAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan Period',
                  layout: 'default',
                  unit: 'years',
                  name: 'interestOnlyLoanPeriodTable',
                  dropDownDependency: 'rehabFinancingAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Amortization',
                  layout: 'default',
                  name: 'amortizationTable',
                  unit: 'years',
                  dropDownDependency: 'rehabFinancingAmount',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: false,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan To Value',
                  layout: 'default',
                  name: 'loanToValueTable',
                  unit: 'years',
                  dropDownDependency: 'rehabFinancingAmount',
                  mainValue: 'percent',
                  displayPercentFrom: ['mortgageAmountTable', 'newMarketValue'],
                  displayPercentFunction: (
                    mortgageAmountTable,
                    purchasePrice
                  ) => {
                    return (
                      yearlyPercentFromYearlyValue(mortgageAmountTable, [
                        purchasePrice,
                      ]) || ' -'
                    );
                  },
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Monthly Payment',
                  layout: 'default',
                  name: 'monthlyPaymentTable',
                  groupEnd: true,
                  unit: '$',
                  dropDownDependency: 'rehabFinancingAmount',
                },
              ],
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan to Value (LTV)',
              name: 'loanToValueBuyAndHold',
              description:
                'The loan amount as a percentage of the property value',
              tooltipHeading: 'Loan to Value (LTV)',
              tooltipFormula: [
                'LTV',
                '=',
                'Loan Amount',
                '/',
                'Property Value',
              ],
              unit: '%',
              derivedFrom: ['totalFinancingAmount', 'newMarketValue'],
              calcFunction: (totalFinancingAmount, newMarketValue) => {
                return yearlyPercentFromYearlyValue(totalFinancingAmount, [
                  newMarketValue,
                ]);
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly Payment',
              layout: 'default',
              name: 'monthlyDebtServiceBuyAndHold',
              derivedFrom: [
                'monthlyDebtService',
                'totalRehabFinancingMonthlyPayment',
                'isRehabFinanced',
              ],
              calcFunction: (
                monthlyDebtService,
                totalRehabFinancingMonthlyPayment,
                isRehabFinanced
              ) => {
                if (isRehabFinanced) {
                  return monthlyDebtService + totalRehabFinancingMonthlyPayment;
                }
                return monthlyDebtService;
              },
              emphasize: true,
              unit: '$',
            },
          ],
        },
        rightSchema: {
          sectionLabel: 'Cash Flow',
          sectionSubLabel: 'Monthly',
          reportButton: 'cashflow_report',
          annualValue: true,
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'Gross Rent',
              name: 'totalRentalIncomeBuyAndHold',
              tooltipHeading: 'Gross Rent',
              description: 'Rental income across all units',

              unit: '$',
              derivedFrom: ['rentalIncomePreVacancy'],
              calcFunction: (rentalIncomePreVacancy) => {
                return rentalIncomePreVacancy;
              },
              dropDown: true,
              tooltipHeading: 'Additional Income',
              condition: [
                {
                  field: 'enterTotalRentalIncome',
                  value: false,
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Gross Rent',
              name: 'totalRentalIncomeBuyAndHold',
              tooltipHeading: 'Gross Rent',
              description: 'Rental income across all units',
              unit: '$',
              derivedFrom: ['rentalIncomePreVacancy'],
              calcFunction: (rentalIncomePreVacancy) => {
                return rentalIncomePreVacancy;
              },
              tooltipHeading: 'Additional Income',
              condition: [
                {
                  field: 'enterTotalRentalIncome',
                  value: true,
                },
              ],
            },
            {
              type: FormUIOptions.Table,
              label: '',
              name: 'grossIncomePerUnit',
              dropDownDependency: 'totalRentalIncomeBuyAndHold',
              condition: [
                {
                  field: 'enterTotalRentalIncome',
                  value: false,
                },
              ],
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'unitName',
                  label: 'Unit Name',
                  dropDownDependency: 'totalRentalIncomeBuyAndHold',
                  derivedFrom: ['unitName'],
                  calFunction: (name) => {
                    return `Unit ${name}`;
                  },
                },
                {
                  type: FormUIOptions.UnitMoney,
                  label: '',
                  showMonthly: true,
                  unit: '$',
                  isConstant: true,
                  displayMonthlyFrom: ['rent'],
                  displayMonthlyFunction: (rent) => {
                    return rent;
                  },
                  dropDownDependency: 'totalRentalIncomeBuyAndHold',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Vacancy',
              layout: 'default',
              name: 'vacancy',
              unit: '$',
              derivedFrom: ['vacancyDeduction'],
              calcFunction: (vacancyDeduction) => {
                const vacancy = convertUnitMoney(vacancyDeduction);
                if (isValue(vacancy)) {
                  return Math.round(vacancy * 100) / 100;
                } else {
                  return 0;
                }
              },
              formula: (vacancy) => ['-', `${vacancy}`],
              displayPercentFrom: [
                'vacancyDeduction',
                'totalRentalIncomeBuyAndHold',
              ],
              displayPercentFunction: (vacancyDeduction, totalRentalIncome) => {
                if (isValue(vacancyDeduction) && isValue(totalRentalIncome)) {
                  return Math.round(
                    (vacancyDeduction / totalRentalIncome) * 100
                  );
                } else {
                  return 0;
                }
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Additional Income',
              name: 'totalAdditionalIncomeBuyAndHold',
              dropDown: true,
              tooltipHeading: 'Additional Income',
              description: 'Additional income except for rent across all units',
              unit: '$',
              derivedFrom: ['totalOtherIncome'],
              calcFunction: (totalOtherIncome) => totalOtherIncome,
              formula: (totalAdditionalIncome) => [
                '+',
                `${totalAdditionalIncome}`,
              ],
            },
            {
              type: FormUIOptions.Table,
              label: '',
              name: 'otherPropertyIncome',
              dropDownDependency: 'totalAdditionalIncomeBuyAndHold',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
                {
                  field: 'enterTotalOtherIncome',
                  value: [false, null, undefined, ''],
                },
              ],
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'otherIncomeName',
                  label: 'Other Income',
                  dropDownDependency: 'totalAdditionalIncomeBuyAndHold',
                },
                {
                  type: FormUIOptions.UnitMoney,
                  label: 'Amount',
                  layout: 'default',
                  name: 'otherIncomeAmount',
                  unit: '$',
                  dropDownDependency: 'totalAdditionalIncomeBuyAndHold',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Gross Income (GOI)',
              name: 'monthlyGOI',
              emphasize: true,
              tooltipHeading: 'Gross Operating Income',
              description:
                'Rental income across all units adjusted for vacancy rate',
              unit: '$',
              derivedFrom: [
                'totalGrossRentalIncome',
                'totalAdditionalIncomeBuyAndHold',
              ],
              calcFunction: (
                totalGrossRentalIncome,
                totalAdditionalIncomeBuyAndHold
              ) => {
                return getSum(
                  totalGrossRentalIncome,
                  totalAdditionalIncomeBuyAndHold
                );
              },
              formula: (monthlyGOI) => ['=', `${monthlyGOI}`],
              total: true,
            },
            {
              type: FormUIOptions.Money,
              label: 'Total Operating Expenses',
              layout: 'default',
              name: 'totalMonthlyOperatingExpenses',
              dropDown: true,
              unit: '$',
              derivedFrom: ['totalOperatingExpenses'],
              calcFunction: (totalOperatingExpenses) => totalOperatingExpenses,
              formula: (totalMonthlyOperatingExpenses) => [
                '-',
                `${totalMonthlyOperatingExpenses}`,
              ],
            },
            {
              type: FormUIOptions.Table,
              label: '',
              name: 'operatingExpenses',
              dropDownDependency: 'totalMonthlyOperatingExpenses',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
                {
                  field: 'enterTotalExpenses',
                  value: [false, null, undefined, ''],
                },
              ],
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'expenseName',
                  label: 'Expense',
                  dropDownDependency: 'totalMonthlyOperatingExpenses',
                },
                {
                  type: FormUIOptions.UnitMoney,
                  derivedFrom: 'rentalIncomePreVacancy',
                  label: 'Amount',
                  layout: 'default',
                  name: 'expenseAmount',
                  unit: '$',
                  dropDownDependency: 'totalMonthlyOperatingExpenses',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Net Income (NOI)',
              layout: 'default',
              name: 'monthlyNOI',
              emphasize: true,
              tooltipHeading: 'Net Operating Income',
              description:
                'Income from all units adjusted for total operating expenses',
              unit: '$',
              derivedFrom: ['monthlyGOI', 'totalMonthlyOperatingExpenses'],
              calcFunction: (monthlyGOI, totalMonthlyOperatingExpenses) => {
                if (isValue(monthlyGOI, totalMonthlyOperatingExpenses)) {
                  return (
                    Math.round(
                      monthlyGOI * 100 - totalMonthlyOperatingExpenses * 100
                    ) / 100
                  );
                } else {
                  return null;
                }
              },
              formula: (monthlyNOI) => ['=', `${monthlyNOI}`],
              total: true,
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly Payment',
              name: 'monthlyDebtServiceBuyAndHold',
              unit: '$',
              formula: (monthlyDebtService) => ['-', `${monthlyDebtService}`],
            },
            {
              type: FormUIOptions.Money,
              label: 'Cash Flow',
              layout: 'default',
              name: 'monthlyPreTaxCashFlowBuyAndHold',
              emphasize: true,
              dynamic: true,
              unit: '$',
              derivedFrom: [
                'monthlyGOI',
                'totalMonthlyOperatingExpenses',
                'monthlyDebtServiceBuyAndHold',
                'cashPurchase',
              ],
              calcFunction: (
                monthlyGOI,
                totalMonthlyOperatingExpenses,
                totalMonthlyMortgagePayment,
                cashPurchase
              ) => {
                if (
                  isValue(
                    monthlyGOI,
                    totalMonthlyOperatingExpenses,
                    totalMonthlyMortgagePayment
                  )
                ) {
                  const monthlyPreTaxIncome =
                    monthlyGOI - totalMonthlyOperatingExpenses;

                  if (cashPurchase) {
                    return monthlyPreTaxIncome;
                  }

                  const monthlyPreTaxCashFlow =
                    monthlyPreTaxIncome - totalMonthlyMortgagePayment;

                  const result = monthlyPreTaxCashFlow;
                  return result;
                } else {
                  return null;
                }
              },
              formula: (annualPreTaxCashFlow) => [
                '=',
                `${annualPreTaxCashFlow}`,
              ],
            },
          ],
        },
      },
    ],
  },
  keyMetrics: [
    {
      sectionValue: 'keyMetrics1',
      layout: 'column',
      schema: [
        {
          type: FormUIOptions.Money,
          label: 'New Value',
          layout: 'default',
          name: 'newMarketValue',
          unit: '$',
          subLabel: [<FaArrowUp className="mb-[0.1rem]" />, <FaArrowDown />],
          subConditionalOn: 'subValue',
          subName: 'valueIncreaseActual',
        },
        {
          type: FormUIOptions.Money,
          label: 'Total Cash Invested',
          tooltipHeading: 'Total Cash Invested',
          description:
            'The total of the original downpayment, closing costs, rehab costs and carrying costs.',
          tooltipFormula: [
            'Total Cash Invested',
            '=',
            'Down Payment',
            '+',
            'Closing Costs',
            '+',
            'Rehab Costs Cash',
            '+',
            'Carrying Costs',
          ],
          name: 'totalCashInvestedBuyAndHold',
          subLabel: [<span>Rehab Cash</span>, <span>Rehab Cash</span>],
          subName: 'rehabCarryingCostsOnlyCash',
        },
        {
          type: FormUIOptions.Money,
          label: 'New Equity',
          description:
            'The new equity in the property after rehab, based on the new value.',
          tooltipHeading: 'New Equity',
          tooltipFormula: ['New Equity', '=', 'New Value', '-', 'Loan Amount'],
          name: 'newEquityBuyAndHold',
        },
        {
          type: FormUIOptions.Money,
          label: 'Cash Flow',
          name: 'monthlyPreTaxCashFlowBuyAndHold',
          tooltipHeading: 'Cash Flow',
          description: 'The monthly cash flow of the property before taxes.',
          tooltipFormula: [
            'Cash Flow',
            '=',
            'Monthly Income',
            '-',
            'Monthly Expenses',
          ],
          dynamic: true,
          isAnnual: false,
          showMonthlyAnnual: true,
        },
      ],
    },
  ],
  investmentMetrics: [
    {
      type: FormUIOptions.Number,
      label: '70% Rule',
      name: 'seventyPercentRuleBuyAndHoldCriteria',
      description:
        'The purchase price should be less than or equal to 70% of the ARV, minus the rehab costs',
      derivedFrom: ['purchasePrice', 'newMarketValue', 'totalRehabCosts'],
      calcFunction: (purchasePrice, newMarketValue, totalRehabCosts) => {
        if (isValue(purchasePrice, newMarketValue, totalRehabCosts)) {
          const result = purchasePrice / (newMarketValue - totalRehabCosts);

          if (result <= 0.7) {
            return {
              value: 1,
              message: `Passes 70% rule (${Math.round(result * 1000) / 10}%).`,
            };
          }
          return {
            value: 0,
            message: `Does not pass 70% rule (${
              Math.round(result * 1000) / 10
            }%).`,
          };
        }
        return {
          value: 2,
          message:
            'Missing value for purchase price, new market value or rehab costs.',
        };
      },

      condition: {
        field: 'screenSeventyPercentRuleBuyAndHold',
        value: true,
      },
    },
    {
      type: FormUIOptions.Money,
      label: 'Total Profit',
      name: 'totalProfitCriteria',
      description:
        'Compare the total profit to the total profit on the purchase (equity + cash flow - cash needed)',
      derivedFrom: [
        'equityOnPurchase',
        'annualPreTaxCashFlow',
        'totalCashInvestedOnPurchase',
        'newEquityBuyAndHold',
        'monthlyPreTaxCashFlowBuyAndHold',
        'totalCashInvestedBuyAndHold',
        'totalProfitBuyAndHold',
      ],
      calcFunction: (
        equityOnPurchase,
        annualPreTaxCashFlow,
        totalCashInvestedOnPurchase,
        newEquityBuyAndHold,
        monthlyPreTaxCashFlowBuyAndHold,
        totalCashInvestedBuyAndHold,
        totalProfitBuyAndHold
      ) => {
        const totalProfitCriteria =
          totalProfitBuyAndHold || criteriaDefaults.totalProfitBuyAndHold;

        if (
          isValue(
            equityOnPurchase,
            annualPreTaxCashFlow,
            totalCashInvestedOnPurchase,
            newEquityBuyAndHold,
            monthlyPreTaxCashFlowBuyAndHold,
            totalCashInvestedBuyAndHold
          )
        ) {
          const totalProfitOnPurchase =
            equityOnPurchase +
            annualPreTaxCashFlow -
            totalCashInvestedOnPurchase;
          const totalProfit =
            newEquityBuyAndHold +
            monthlyPreTaxCashFlowBuyAndHold * 12 -
            totalCashInvestedBuyAndHold;

          const difference = totalProfit - totalProfitOnPurchase;

          if (difference >= totalProfitCriteria) {
            return {
              value: 1,
              message: `Total profit of $${getNumberWithCommas(
                Math.round(totalProfit)
              )} is $${getNumberWithCommas(
                Math.round(difference)
              )} higher than the initial of $${getNumberWithCommas(
                Math.round(totalProfitOnPurchase)
              )}.`,
            };
          }
          return {
            value: 0,
            message: `Total profit of $${getNumberWithCommas(
              Math.round(totalProfit)
            )} is $${getNumberWithCommas(
              Math.round(difference)
            )} different than the initial of $${getNumberWithCommas(
              Math.round(totalProfitOnPurchase)
            )}.`,
          };
        }

        return {
          value: 2,
          message: 'Missing value for equity, cash flow, or cash needed.',
        };
      },

      condition: {
        field: 'screenTotalProfitBuyAndHold',
        value: true,
      },
    },
    {
      type: FormUIOptions.Money,
      label: 'Cash Needed',
      description:
        'Maximum allowable cash needed (downpayment + closing costs).',
      name: 'cashNeededCriteria',
      derivedFrom: [
        'totalCashInvestedBuyAndHold',
        'cashNeededBuyAndHoldCriteria',
        'purchasePrice',
      ],
      calcFunction: (
        totalCashInvestedOnPurchase,
        maxCashNeededPurchase,
        purchasePrice
      ) => {
        const maxCashNeeded = convertUnitMoney(
          maxCashNeededPurchase ||
            criteriaDefaults.cashNeededBuyAndHoldCriteria,
          { purchasePrice }
        );

        if (isValue(totalCashInvestedOnPurchase, maxCashNeeded)) {
          if (totalCashInvestedOnPurchase > maxCashNeeded) {
            return {
              value: 0,
              message: `Cash needed of $${getNumberWithCommas(
                Math.round(totalCashInvestedOnPurchase)
              )} is higher than the maximum of $${getNumberWithCommas(
                maxCashNeeded
              )}.`,
            };
          }
          if (totalCashInvestedOnPurchase < maxCashNeededPurchase) {
            return {
              value: 1,
              message: `Cash needed of $${getNumberWithCommas(
                Math.round(totalCashInvestedOnPurchase)
              )} is lower than the maximum of $${getNumberWithCommas(
                maxCashNeeded
              )}.`,
            };
          }
        }
        return {
          value: 2,
          message: 'Missing value for cash needed or maximum cash needed.',
        };
      },
      condition: {
        field: 'screenCashNeededBuyAndHold',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: '1% Rule',
      name: 'onePercentRuleCriteria',
      description:
        'The monthly gross rent should be at least 1% of the purchase price.',
      derivedFrom: ['monthlyGOI', 'purchasePrice'],
      calcFunction: (monthlyGOI, purchasePrice) => {
        if (isValue(monthlyGOI, purchasePrice)) {
          const monthlyGOIValue = getNumberFromString(monthlyGOI);
          const purchasePriceValue = getNumberFromString(purchasePrice);

          const result = monthlyGOIValue / purchasePriceValue;
          if (result >= 0.01) {
            return {
              value: 1,
              message: `Passes 1% rule (${Math.round(result * 1000) / 10}%).`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass 1% rule (${
                Math.round(result * 1000) / 10
              }%).`,
            };
          }
        }
        return {
          value: 2,
          message: 'Missing value for monthly GOI or purchase price.',
        };
      },
      condition: {
        field: 'screenOnePercentRuleBuyAndHold',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: '50% Rule',
      name: 'fiftyPercentRuleCriteria',
      description:
        'The monthly expenses should be less than or equal to 50% of the monthly gross rent.',
      derivedFrom: ['totalMonthlyOperatingExpenses', 'monthlyGOI'],
      calcFunction: (totalMonthlyOperatingExpenses, monthlyGOI) => {
        if (isValue(totalMonthlyOperatingExpenses, monthlyGOI)) {
          const totalMonthlyOperatingExpensesValue = getNumberFromString(
            totalMonthlyOperatingExpenses
          );
          const monthlyGOIValue = getNumberFromString(monthlyGOI);

          const result = totalMonthlyOperatingExpensesValue / monthlyGOIValue;
          if (result <= 0.5) {
            return {
              value: 1,
              message: `Passes 50% rule (${Math.round(result * 1000) / 10}%).`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass 50% rule (${
                Math.round(result * 1000) / 10
              }%).`,
            };
          }
        }
        return {
          value: 2,
          message: 'Missing value for monthly expenses or monthly GOI.',
        };
      },
      condition: {
        field: 'screenFiftyPercentRuleBuyAndHold',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: 'Cash Flow',
      name: 'cashFlowCriteria',
      description:
        'Whether the property passes the minimum cash flow requirement.',
      derivedFrom: ['monthlyPreTaxCashFlowBuyAndHold', 'minCashFlowBuyAndHold'],
      calcFunction: (monthlyPreTaxCashFlow, minCashFlowPurchase) => {
        const minCashFlow =
          minCashFlowPurchase || criteriaDefaults.minCashFlowBuyAndHold;

        if (isValue(monthlyPreTaxCashFlow, minCashFlow)) {
          if (monthlyPreTaxCashFlow >= minCashFlow) {
            return {
              value: 1,
              message: `Passes cash flow rule ($${getNumberWithCommas(
                Math.round(monthlyPreTaxCashFlow)
              )}).`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass cash flow rule ($${getNumberWithCommas(
                Math.round(monthlyPreTaxCashFlow)
              )}).`,
            };
          }
        }
        return {
          value: 2,
          message: 'Missing value for monthly cash flow.',
        };
      },
      condition: {
        field: 'screenCashFlowBuyAndHold',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: 'Cap Rate (Purchase)',
      name: 'capRateCriteria',
      description: 'Whether the property passes the cap rate requirement.',
      derivedFrom: ['capRate', 'minCapRateBuyAndHold'],
      calcFunction: (capRateInitial, minCapRatePurchase) => {
        const minCapRate =
          minCapRatePurchase || criteriaDefaults.minCapRateBuyAndHold;

        if (isValue(capRateInitial, minCapRate)) {
          if (capRateInitial >= minCapRate) {
            return {
              value: 1,
              message: `Passes cap rate rule (${capRateInitial}%)`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass cap rate rule (${capRateInitial}%)`,
            };
          }
        }
        return {
          value: 2,
          message: 'Missing value for cap rate or minimum cap rate',
        };
      },
      condition: {
        field: 'screenCapRateBuyAndHold',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: 'Total ROI Year 1',
      name: 'totalRoiYear1Criteria',
      description:
        'Whether the property passes the total return on investment requirement.',
      derivedFrom: [
        'totalROIYear1BuyandHold',
        'totalCashInvestedBuyAndHold',
        'minTotalRoiYear1BuyAndHold',
      ],
      calcFunction: (
        totalRoiYear1,
        totalCashInvested,
        minTotalRoiYear1BuyAndHold
      ) => {
        const minTotalRoiYear1 =
          minTotalRoiYear1BuyAndHold ||
          criteriaDefaults.minTotalRoiYear1BuyAndHold;

        if (isValue(totalRoiYear1, minTotalRoiYear1, totalCashInvested)) {
          const value = (totalRoiYear1 / totalCashInvested) * 100;

          if (value >= minTotalRoiYear1) {
            return {
              value: 1,
              message: `Passes total ROI year 1 rule (${Math.round(value)}%)`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass total ROI year 1 rule (${Math.round(
                value
              )}%)`,
            };
          }
        }
        return {
          value: 2,
          message:
            'Missing value for total ROI year 1 or minimum total ROI year 1',
        };
      },
      condition: {
        field: 'screenTotalRoiYear1BuyAndHold',
        value: true,
      },
    },
  ],
};
